import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import PropTypes from "prop-types";

import api from "api/api";

// import Mui components
// import { useTheme } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
// import mui icons
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";

//import components
import ConsumerFilter from "../../common/ConsumerFilter";
import ProductInfo from "../../common/ProductInfo";
// utils
import SortFinalData from "../../../utils/SortFinalData";
import GetMacroCategoryOrder from "../../../utils/GetMacroCategoryOrder";

import WeightToggleIcon from "../../../assets/icons/weightToggle.png";
import TextThemeIcon from "../../../assets/icon-for-text-theam.png"
import ColumnVisibility from "components/common/ColumnVisibility";

// redux
import { useSelector, useDispatch } from "react-redux";
import { toggleTextThemeSelection } from "store/features/textThemeSlice";
import { loadProductOrderState } from "store/features/productsInfoSlice";
import HeaderSampleSize from "./HeaderSampleSize";
import DragRenderTable from "./DragRenderTable";
import SkewTabs from "./SkewTabs";
import RemoveRestoreBvCheckbox from "./removeRestore/RemoveRestoreBvCheckbox";
import AnalysisTableBottomLabel from "./AnalysisTableBottomLabel";
import RemoveRestoreDialog from "./removeRestore/RemoveRestoreDialog";
import ClaimsTerritoryFilter from "components/common/ClaimsTerritoryFilter";
import OverlappingScales from "./boxAnalysis/OverlappingScales";
import ProductSorting from "./ProductSorting";

import {
    getRecallAndITM,
    getConceptAndRecall,
    getProductScoreSumObject,
    getProductScoreWithNaAndRowSpan,
    getScoreByVariable,
    getProductColumnClassName,
    getViewKeys,
    getOralCareProductCodeTableHeader,
    getSelectedOption,
    getOverallCalculationResult,
    highlightText,
    getFormattedScoreOrNA,
    gapRow,
    getDataView,
    getSortedDataMapping,
    getAlphabeticalMacroCategories,
    isConceptVariable,
    isLabelOrSiblingRemoved,
} from "../../../utils/analysisTable/analysisUtils";

import { getTestResultByVariable, getColorsAndLetter } from "../../../utils/analysisTable/tTestutils";
import { showNotification } from "store/features/notificationSlice";
import {
    loadOverallAbsoluteToggleState,
    loadSecondaryAbsoluteToggleState,
    loadAgreementAbsoluteToggleState,
    loadClaimsAbsoluteToggleState,
} from "store/features/analysisResultsSlice";


import TableCellWithInnerSeparator from "./TableCellWithInnerSeparator";
import TableCellWithOuterSeparator from "./TableCellWithOuterSeparator";

function AnalysisTable({ productsSecondaryData = [], handleFilterSubmit = () => { }, analysisLevelData = {}, tTest = {}, settings = {} }) {
    const dispatch = useDispatch();
    const { isITMDataShown = false, tableConfig, moduleName, boxAnalysisTypeSelection = [], boxAnalysisSelection = {}, analysisTypeValue, mappings, claimsTerritoryTags = [], claimsTerritorySelectedTags = [], penaltyToggle = false } = analysisLevelData;
    const { tTestLegends, isFisher, tTestResult, testLabel } = tTest;
    const { activeTab, setActiveTab } = settings;
    const { stickyColumns = [] } = tableConfig;
    const isConsumerFilterLoadingStoreData = useSelector((state) => state.consumerFilterStore.isLoading);
    const weightToggle = useSelector((state) => state.analysisResultsStore.weightToggle);
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);

    const overallToggle = useSelector((state) => state.analysisResultsStore.overallToggle);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);

    const productColumnVisibility = useSelector((state) => state.productsInfoStore.productsVisibility);
    const productsOrder = useSelector((state) => state.productsInfoStore.productsOrder);
    const textThemeSelections = useSelector((state) => state.textThemeStore.selections);
    // tTest
    const testAnalysisType = useSelector((state) => state.tTestStore.testAnalysisType);

    // dataview
    const overallAnalysisTypeValue = useSelector((state) => state.analysisTypeSelectionStore.overallAnalysisTypeValue);

    const hiddenProductsCount = Object.values(productColumnVisibility || {}).filter((datum) => datum === true)?.length || 0;

    const isDeodorants =
        productCategoryData?.label?.toLowerCase() === "deodorants" ||
        process.env?.REACT_APP_DEODORANTS?.toLowerCase() === productCategoryData?.label?.toLowerCase();

    const isSingleStudy = Array.isArray(selectedStudy) && selectedStudy.length === 1;
    const enableTextThemeAnalysis = isDeodorants && isSingleStudy && moduleName !== "claims";


    const hasITMTextTheme = useMemo(() => {
        if (!enableTextThemeAnalysis || !isITMDataShown) return false;
        const itmVariables = new Set();
        if (mappings && Array.isArray(mappings)) {
            mappings.forEach(macro => {
                macro.displayLabels?.forEach(bv => {
                    if (bv.variableCategory === "KPI ITM") {
                        itmVariables.add(bv.backendVariable);
                    }
                });
            });
        }
        return productsSecondaryData.some(datum =>
            datum.scores?.some(score =>
                score.text_theme_check_box === true &&
                (itmVariables.has(score.backendVariable))
            )
        );
    }, [enableTextThemeAnalysis, isITMDataShown, productsSecondaryData, mappings]);

    const hasRecallTextTheme = useMemo(() => {
        if (!enableTextThemeAnalysis) return false;
        const recallVariables = new Set();
        if (mappings && Array.isArray(mappings)) {
            mappings.forEach(macro => {
                macro.displayLabels?.forEach(bv => {
                    if (bv.variableCategory === "KPI (OO)") {
                        recallVariables.add(bv.backendVariable);
                    }
                });
            });
        }
        return productsSecondaryData.some(datum =>
            datum.scores?.some(score =>
                score.text_theme_check_box === true &&
                (recallVariables.has(score.backendVariable))
            )
        );
    }, [enableTextThemeAnalysis, isITMDataShown, productsSecondaryData, mappings]);

    const hasStandardTextTheme = useMemo(() => {
        if (!enableTextThemeAnalysis || isITMDataShown) return false;
        // if (["overall", "claims", "fragranceMetrics"].includes(moduleName)) return false;
        const STANDARD_TT_CATEGORIES = ["Secondary Attribute", "Agreement KPI"];
        const standardVariables = new Set();
        if (mappings && Array.isArray(mappings)) {
            mappings
                .filter(macro => !activeTab || macro.macroCategory === activeTab)
                .forEach(macro => {
                    macro.displayLabels?.forEach(bv => {
                        if (STANDARD_TT_CATEGORIES.includes(bv.variableCategory)) {
                            standardVariables.add(bv.backendVariable);
                        }
                    });
                });
        }

        return productsSecondaryData.some(datum =>
            datum.scores?.some(score =>
                score.text_theme_check_box === true &&
                standardVariables.has(score.backendVariable)
            )
        );
    }, [enableTextThemeAnalysis, productsSecondaryData, mappings, activeTab, moduleName]);

    const rowLevelTTColSpan = isITMDataShown
        ? (hasITMTextTheme ? 1 : 0) + (hasRecallTextTheme ? 1 : 0)
        : (moduleName === "overall"
            ? (hasRecallTextTheme ? 1 : 0)
            : (hasStandardTextTheme ? 1 : 0));

    const textThemeHeaderStyles = {
        main: {
            minWidth: 36,
            maxWidth: 36,
            width: 36,
            padding: "4px 2px",
        },
        wrapper: {
            padding: 0,
            height: "100%",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        value: {
            justifyContent: "center",
            alignItems: "center",
            background: "rgba(57, 131, 243, 0.10)",
            color: "#005eef",
            boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
            borderRadius: "6px",
            width: "26px",   // SMALLER
            height: "26px",  // SMALLER
            display: "flex",
        }
    };

    const TTIconWithTooltip = (
        <Tooltip
            arrow
            placement="top"
            title={
                <div
                    style={{
                        textAlign: "center",
                        fontWeight: 600,
                        fontSize: "12px",
                        lineHeight: "20px",
                    }}
                >
                    Theme Text Analysis powered by
                    <br />
                    Gen AI - Chat GPT 5
                </div>
            }
            componentsProps={{
                tooltip: {
                    sx: {
                        backgroundColor: "#f0f8ff",
                        color: "#3A3A3A",
                        border: "1px solid #4A90FF",
                        borderRadius: "8px",
                        padding: "12px 18px",
                        boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
                        maxWidth: "320px",
                    },
                },
                arrow: {
                    sx: {
                        color: "#f0f8ff",
                        "&:before": {
                            border: "1px solid #4A90FF",
                        },
                    },
                },
            }}
        >
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <img
                    src={TextThemeIcon}
                    alt="Text Theme"
                    style={{
                        width: "18px",
                        height: "18px",
                        borderWidth: "3px",
                        objectFit: "contain",
                        marginLeft: "8px",
                        filter: "invert(45%) sepia(80%) saturate(800%) hue-rotate(195deg) brightness(100%)",
                    }}
                />
            </span>
        </Tooltip>
    );
    const [productDetailsDialogueOpen, setProductDetailsDialogueOpen] = useState(false);
    const likertScaleData = useSelector((state) => state.analysisResultsStore.likertScaleData);
    const productsTestCodeMapData = useSelector((state) => state.studyDataStore.productTestCodeMap);
    const defaultOverallAnalysisResultState = useSelector((state) => state.analysisResultsStore.defaultOverallAnalysisResult);
    const defaultSecondaryAnalysisResultState = useSelector((state) => state.analysisResultsStore.defaultSecondaryAnalysisResult);
    const defaultAgreementAnalysisResultState = useSelector((state) => state.analysisResultsStore.defaultAgreementAnalysisResult);

    // column drag and drop variables
    const [draggableColumns, setDraggableColumns] = useState(productsSecondaryData);
    const [draggedColumnIndex, setDraggedColumnIndex] = useState(null);
    const [draggedColumnDiamension, setDraggedColumnDiamension] = useState({ width: null, height: null });
    const [targetColumnIndex, setTargetColumnIndex] = useState(null);

    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [hasActuallyMoved, setHasActuallyMoved] = useState(false);

    const tableRef = useRef(null);
    const tbodyRef = useRef(null);
    const draggedElementRef = useRef(null);
    const initialMousePos = useRef(null);

    const [actionStandardKpis, setActionStandardKpis] = useState([]);
    const [actionStandardData, setActionStandardData] = useState([]);

    const [showCheckBox, setShowCheckBox] = useState(false);
    const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
    const removedKpiStoreState = useSelector((state) => state.analysisKpiStore[`${moduleName}RemovedKpiList`]);
    const absoluteToggle = useSelector((state) => state.analysisResultsStore[`${moduleName}AbsoluteToggle`]);

    const [removedKpi, setRemovedKpi] = useState([]);
    const [disableRestore, setDisableRestore] = useState(true);
    const [totalSampleSize, setTotalSampleSize] = useState(0);
    const [totalVisibleProducts, setTotalVisibleProducts] = useState(0);
    // const [checkedTTBoxes, setCheckedTTBoxes] = useState({});
    let noOfProducts = productsSecondaryData.length;
    const role = useSelector((state) => state.userStore.role) || null;

    // const isDeodorants = process.env?.REACT_APP_DEODORANTS?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isOralCare = process.env?.REACT_APP_ORAL_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCleansing = process.env?.REACT_APP_SKIN_CLEANSING?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isHairCare = process.env?.REACT_APP_HAIR_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCare = process.env?.REACT_APP_SKIN_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isUnileverFragranceHouse = process.env?.REACT_APP_UNILEVER_FRAGRANCE_HOUSE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isBeautyAndWellbeing = moduleName === "justRight" && (isHairCare || isSkinCare || isUnileverFragranceHouse);

    const isBAndW = isHairCare || isSkinCare || isUnileverFragranceHouse;
    const customOrderMacroCategory = GetMacroCategoryOrder(productCategoryData?.label?.toLowerCase(), moduleName);

    // ==================== CONCEPT/RECALL DROPDOWN STATE ====================
    const selectedDataset = useSelector((state) => state.analysisResultsStore.selectedDataset);
    const isConceptAndRecallFeature = isHairCare && moduleName !== "claims" && moduleName !== "fragranceMetrics" && moduleName !== "justRight";
    const showConceptData = isConceptAndRecallFeature && selectedDataset?.value === "concept";
    const showRecallData = isConceptAndRecallFeature && selectedDataset?.value === "recall";
    const showConceptAndRecallData = isConceptAndRecallFeature && selectedDataset?.value === "concept_recall";
    const isConceptRecallShown = isHairCare && (showConceptData || showRecallData || showConceptAndRecallData);
    const conceptRecallMultiplier = showConceptAndRecallData ? 2 : 1;

    const [tableHeadSpan, setTableHeadSpan] = useState(1);
    // const [tableColSpan, setTableColSpan] = useState(1);
    const [box, setBox] = useState(false);

    const [dataViewKey, setDataViewKey] = useState(""); //ORDER [top:top_weight:mean:weight_mean, bottom:bottom_weight]
    const [tableDataMapping, setTableDataMapping] = useState([]);

    // input search
    const [searchValue, setSearchValue] = useState("");
    const [selectedProductInfo, setSelectedProductInfo] = useState({});
    // const [activeTab, setActiveTab] = useState(null);
    const [baseVariable, setBaseVariable] = useState("");
    const [order, setOrder] = useState("");
    const hasTextThemeSelections = textThemeSelections.some(item => item.isSelected === true);

    const defaultAnalysisResultForModule = useMemo(() => {
        if (moduleName === "secondary") return defaultSecondaryAnalysisResultState;
        if (moduleName === "agreement") return defaultAgreementAnalysisResultState;
        return defaultOverallAnalysisResultState;
    }, [moduleName, defaultOverallAnalysisResultState, defaultSecondaryAnalysisResultState, defaultAgreementAnalysisResultState]);

    const originalSampleSizeMap = useMemo(() => {
        const map = {};
        (defaultAnalysisResultForModule || []).forEach((item) => {
            map[`${item.title}||${item.coreDataId}`] = item.sampleSize;
        });
        return map;
    }, [defaultAnalysisResultForModule]);

    const getOriginalSampleSize = (datum) => {
        const key = `${datum?.title}||${datum?.coreDataId}`;
        const found = originalSampleSizeMap[key];
        return found ?? datum?.sampleSize;
    };

    const showSampleSizeNote = useMemo(() => {
        return productsSecondaryData.some(ele => getOriginalSampleSize(ele) <= 10 && ele.scores?.some(ele => ele.text_theme_check_box));
    }, [productsSecondaryData, originalSampleSizeMap]);

    useEffect(() => {
        let sampleTotal = 0;
        let visibleProducts = 0;
        draggableColumns.forEach((datum) => {
            const classNameKey = `checked--${datum.title.split(" ").join("_")}__${datum.coreDataId}`;
            if (!productColumnVisibility?.[classNameKey]) {
                sampleTotal += datum.sampleSize;
                visibleProducts += 1;
            }
        });
        setTotalSampleSize(sampleTotal);
        setTotalVisibleProducts(visibleProducts);
    }, [draggableColumns, productColumnVisibility]);

    useEffect(() => {
        setRemovedKpi([...removedKpiStoreState]);
        setDisableRestore(removedKpiStoreState.length === 0);
    }, [moduleName, removedKpiStoreState]);

    // useEffect(() => {
    //     onTextThemeSelectionChange(checkedTTBoxes);
    // }, [checkedTTBoxes, onTextThemeSelectionChange]);

    useEffect(() => {
        let macroCategoryOrder = customOrderMacroCategory;
        if (!macroCategoryOrder && moduleName !== "claims") {
            macroCategoryOrder = getAlphabeticalMacroCategories(mappings);
        }
        let sortedData = getSortedDataMapping(mappings, macroCategoryOrder, moduleName, isSkinCleansing || isOralCare || isUnileverFragranceHouse, isUnileverFragranceHouse);

        if (moduleName !== "claims") {
            if (activeTab) {
                sortedData = sortedData.filter((each) => each.macroCategory === activeTab);
            }
            // Only update if different to avoid infinite loop
            setTableDataMapping((prev) => {
                const isEqual = JSON.stringify(prev) === JSON.stringify(sortedData);
                return isEqual ? prev : sortedData;
            });
        } else {
            if (searchValue) {
                const filteredData = sortedData.filter((item) => item.displayLabels[0].displayLabel.toLowerCase().includes(searchValue.toLowerCase()));
                setTableDataMapping([...filteredData]);
            } else {
                setTableDataMapping([...sortedData]);
            }
        }
    }, [searchValue, customOrderMacroCategory, mappings, activeTab, moduleName, isSkinCleansing, isOralCare, isUnileverFragranceHouse]);

    useEffect(() => {
        const _dataViewKey = getDataView(analysisTypeValue, weightToggle, boxAnalysisTypeSelection);
        const { value } = analysisTypeValue;
        setBox(value === "box_analysis");
        if (value !== "box_analysis") {
            resetAbsoluteCountToggle()
        }
        setDataViewKey(_dataViewKey);
    }, [analysisTypeValue, boxAnalysisTypeSelection, weightToggle]);

    // ==================== UPDATED tableHeadSpan with conceptRecallMultiplier ====================
    useEffect(() => {
        const { value } = analysisTypeValue;
        setBox(value === "box_analysis");
        if (value === "box_analysis") {
            if (isITMDataShown) {
                // setTableColSpan(2);
                setTableHeadSpan(boxAnalysisTypeSelection.length * 2 * conceptRecallMultiplier);
            } else {
                // setTableColSpan(1);
                setTableHeadSpan(boxAnalysisTypeSelection.length * conceptRecallMultiplier);
            }
        } else if (value === "allThreeBox") {
            setTableHeadSpan((penaltyToggle ? 5 : 3) * conceptRecallMultiplier);
        } else {
            if (isITMDataShown) {
                // setTableColSpan(1);
                setTableHeadSpan(2 * conceptRecallMultiplier);
            } else {
                // setTableColSpan(1);
                setTableHeadSpan(1 * conceptRecallMultiplier);
            }
        }
    }, [analysisTypeValue, isITMDataShown, boxAnalysisTypeSelection, penaltyToggle, conceptRecallMultiplier]);

    useEffect(() => {
        setDraggableColumns(SortFinalData(productsSecondaryData, productsOrder));
    }, [productsSecondaryData, productsOrder]);

    useEffect(() => {
        const [key1] = getViewKeys(dataViewKey);
        if (baseVariable !== "" && order !== "" && draggableColumns.length) {
            // setDraggableColumns(SortFinalData(productsSecondaryData, productsOrder));
            setDraggableColumns(sortArrayByBv(draggableColumns, baseVariable, key1, order))
        }
    }, [baseVariable, order, dataViewKey]);

    const resetAbsoluteCountToggle = () => {
        if (moduleName === "overall") {
            dispatch(loadOverallAbsoluteToggleState(false));
        } else if (moduleName === "secondary") {
            dispatch(loadSecondaryAbsoluteToggleState(false));
        } else if (moduleName === "agreement") {
            dispatch(loadAgreementAbsoluteToggleState(false));
        } else if (moduleName === "claims") {
            dispatch(loadClaimsAbsoluteToggleState(false));
        }
    };

    const handleSetProductSorting = (variable, productsSortOrder) => {
        setBaseVariable(variable);
        setOrder(productsSortOrder);
    }

    function sortArrayByBv(arr, bv, field = "mean", order = "asc") {
        return [...arr].sort((a, b) => {
            const valueA = parseFloat(
                a.scores.find(item => item.backendVariable === bv)?.[field] || 0
            );

            const valueB = parseFloat(
                b.scores.find(item => item.backendVariable === bv)?.[field] || 0
            );

            return order === "asc" ? valueA - valueB : valueB - valueA;
        });
    }

    const addBorders = useCallback(() => {
        // Check if the tbody element is available
        if (tbodyRef.current) {
            // Get all <tr> (table row) elements within the tbody
            const rows = Array.from(tbodyRef.current.querySelectorAll("tr.score-row"));
            let currentGroupLastRowIndex = 0;
            rows.forEach((row, index) => {
                const tds = row.querySelectorAll("td");
                const firstTd = row.querySelector("td");
                tds.forEach((td, i) => {
                    // clear all border styles
                    td.style.borderTop = "";
                    td.style.borderBottom = "";
                    td.style.borderRight = "";
                    td.style.borderLeft = "";
                    td.style.borderTopLeftRadius = "";
                    td.style.borderTopRightRadius = "";
                    td.style.borderBottomLeftRadius = "";
                    td.style.borderBottomRightRadius = "";
                    td.style.borderBottomWidth = "0px";

                    if (firstTd.hasAttribute("rowspan")) {
                        currentGroupLastRowIndex = index + firstTd.rowSpan - 1;
                        firstTd.style.borderTopLeftRadius = "8px"; // Round the top left corner
                        firstTd.style.borderBottomLeftRadius = "8px"; // Round the bottom left corner
                        firstTd.style.borderLeft = "2px solid #005eef80"; // Add border on the left
                        firstTd.style.borderBottom = "2px solid #005eef80";
                        td.style.borderTop = "2px solid #005eef80";
                        if (i === tds.length - 1) {
                            td.style.borderTopRightRadius = "8px"; // Round the top left corner
                            td.style.borderRight = "2px solid #005eef80";
                        }
                    } else if (i === tds.length - 1) {
                        td.style.borderRight = "2px solid #005eef80";
                    }

                    // identifying the last row and put bottom border
                    if (currentGroupLastRowIndex === index && i < tds.length - 1) {
                        td.style.borderBottom = "2px solid #005eef80";
                    } else if (currentGroupLastRowIndex === index && i === tds.length - 1) {
                        td.style.borderBottomRightRadius = "8px";
                        td.style.borderBottom = "2px solid #005eef80";
                    }
                });
            });
        }
    }, []);

    // useEffect hook to call addBorders after a delay upon certain dependencies changing
    useEffect(() => {
        if (moduleName === "overall" && !isBAndW && !isBeautyAndWellbeing) {
            // Set a timeout to delay the execution of addBorders function
            const addBr = setTimeout(() => {
                if (!hasActuallyMoved) {
                    addBorders(); // Call the addBorders function after the delay
                }
            }, 10); // Wait for 10 milliseconds

            // Cleanup function to clear the timeout if the component unmounts or dependencies change
            return () => clearTimeout(addBr);
        }
    }, [addBorders, mappings, draggableColumns, productColumnVisibility, overallToggle, removedKpi, hasActuallyMoved, overallAnalysisTypeValue, moduleName, isITMDataShown]); // Dependencies that trigger the effect

    const getActionStandard = useCallback(() => {
        if (selectedStudy.length === 1 && (moduleName === "overall" || (moduleName === "agreement" && isBAndW) || (moduleName === "justRight" && (isHairCare || isUnileverFragranceHouse)))) {
            const coreDataId = selectedStudy[0]?.id || null;
            if (coreDataId) {
                const url = process.env.REACT_APP_SERVER_PATH + `/action-standard-groups/submit/kpis/?archive=False&core_data_model=${coreDataId}`;
                try {
                    api.get(url)
                        .then(async (res) => {
                            const response = res.data;
                            const kpis = new Set(
                                response
                                    .map((datum) => {
                                        const _kpi = datum?.action_standard_kpis?.map((item) => {
                                            const kpiVal = item?.kpi;
                                            if (Array.isArray(kpiVal)) {
                                                const BW = kpiVal.map((bv) => {
                                                    if (bv && typeof bv === "object" && "backend_variable" in bv) return bv.backend_variable;
                                                    return bv;
                                                });
                                                return isBAndW ? BW : kpiVal;
                                            }
                                            return kpiVal || [];
                                        });
                                        return _kpi.flat();
                                    })
                                    .flat()
                            );
                            setActionStandardKpis([...kpis]);
                            setActionStandardData(response);
                        })
                        .catch(async (error) => {
                            handleError("error", error);
                        });
                } catch (error) {
                    handleError("error", error);
                }
            }
        }
    }, [selectedStudy, moduleName, isOralCare, isBAndW]);

    useEffect(() => {
        getActionStandard();
    }, [getActionStandard]);

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    const returnTableHeader = () => {
        const [key1, key2, key3] = getViewKeys(dataViewKey);
        return (
            <tr>
                {stickyColumns.map((col, i) => {
                    return i === 0 ? (
                        <TableCellWithInnerSeparator
                            key={col + i}
                            value={
                                stickyColumns.length === 1 ? (
                                    <>
                                        {col}
                                        {isOralCare &&
                                            <ProductSorting
                                                dataMapping={tableDataMapping}
                                                analysisLevelData={{
                                                    box: box,
                                                    key1: key1,
                                                    key2: key2,
                                                    key3: key3,
                                                    moduleName: moduleName,
                                                    noOfProducts: noOfProducts,
                                                    removedKpiStoreState: removedKpiStoreState
                                                }}
                                                settings={{
                                                    isITMDataShown: isITMDataShown,
                                                }}
                                                productsSecondaryData={draggableColumns}
                                                handleProductSorting={handleSetProductSorting}
                                            />
                                        }
                                    </>
                                ) : col
                            }
                            cellType="th"
                            testLabel=""
                            styles={{
                                main: {
                                    minWidth: moduleName === "claims" ? 300 : 200,
                                    maxWidth: moduleName === "claims" ? 300 : 200,
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 2,
                                },
                                wrapper: {},
                                value: {
                                    alignItems: "center",
                                },
                            }}
                        />
                    ) : (
                        <TableCellWithOuterSeparator
                            key={col + i}
                            value={
                                stickyColumns.length > 1 ? (
                                    <>
                                        {col}
                                        {isOralCare &&
                                            <ProductSorting
                                                dataMapping={tableDataMapping}
                                                analysisLevelData={{
                                                    box: box,
                                                    key1: key1,
                                                    key2: key2,
                                                    key3: key3,
                                                    moduleName: moduleName,
                                                    noOfProducts: noOfProducts,
                                                    removedKpiStoreState: removedKpiStoreState
                                                }}
                                                settings={{
                                                    isITMDataShown: isITMDataShown,
                                                }}
                                                productsSecondaryData={draggableColumns}
                                                handleProductSorting={handleSetProductSorting}
                                            />
                                        }
                                    </>
                                ) : col
                            }
                            cellType="th"
                            testLabel=""
                            styles={{
                                main: {
                                    minWidth: 300,
                                    maxWidth: moduleName === "claims" ? 500 : 300,
                                    position: "sticky",
                                    left: moduleName === "claims" ? 300 : 200,
                                    zIndex: 2,
                                },
                                wrapper: {
                                    justifyContent: "space-between",
                                },
                                value: {
                                    alignItems: "center",
                                },
                            }}
                        />
                    );
                })}
                {box && (
                    <TableCellWithOuterSeparator
                        value="Box Type Analysis"
                        cellType="th"
                        testLabel=""
                        mainProps={{
                            colSpan: boxAnalysisTypeSelection.length,
                        }}
                        styles={{
                            main: {
                                minWidth: 200,
                                maxWidth: 200,
                                zIndex: 2,
                            },
                            wrapper: {
                                justifyContent: "center",
                            },
                            value: {
                                justifyContent: "center",
                                alignItems: "center",
                            },
                        }}
                    />
                )}
                {showConceptAndRecallData && overallToggle && !["claims"].includes(moduleName) && (
                    <>
                        {/* Concept Overall Sample */}
                        <TableCellWithOuterSeparator
                            value={
                                <>
                                    <div style={{ textAlign: "center" }}>
                                        Concept Overall Sample <br />
                                        <span className="text-xs">(All Products Combined)</span>
                                    </div>

                                    <HeaderSampleSize
                                        draggableColumns={draggableColumns}
                                        productColumnVisibility={productColumnVisibility}
                                        isProductHeader={false}
                                    />
                                </>
                            }
                            cellType="th"
                            testLabel=""
                            mainProps={{
                                colSpan: box ? boxAnalysisTypeSelection.length : isBeautyAndWellbeing ? (penaltyToggle ? 5 : 3) : 1,
                                align: "center",
                            }}
                            styles={{
                                main: {
                                    minWidth: 200,
                                    maxWidth: 200,
                                    zIndex: 2,
                                },
                                wrapper: {},
                                value: {
                                    justifyContent: "space-between",
                                    flexDirection: "column",
                                },
                            }}
                        />

                        {/* Recall Overall Sample */}
                        <TableCellWithOuterSeparator
                            value={
                                <>
                                    <div style={{ textAlign: "center" }}>
                                        Recall Overall Sample <br />
                                        <span className="text-xs">(All Products Combined)</span>
                                    </div>

                                    <HeaderSampleSize
                                        draggableColumns={draggableColumns}
                                        productColumnVisibility={productColumnVisibility}
                                        isProductHeader={false}
                                    />
                                </>
                            }
                            cellType="th"
                            testLabel=""
                            mainProps={{
                                colSpan: box ? boxAnalysisTypeSelection.length : isBeautyAndWellbeing ? (penaltyToggle ? 5 : 3) : 1,
                                align: "center",
                            }}
                            styles={{
                                main: {
                                    minWidth: 200,
                                    maxWidth: 200,
                                    zIndex: 2,
                                },
                                wrapper: {},
                                value: {
                                    justifyContent: "space-between",
                                    flexDirection: "column",
                                },
                            }}
                        />
                    </>
                )}
                {isITMDataShown && overallToggle && !showConceptAndRecallData && !["claims"].includes(moduleName) && (
                    <TableCellWithOuterSeparator
                        value={
                            <>
                                <div
                                    style={{
                                        textAlign: "center",
                                    }}
                                >
                                    ITM Overall Sample <br /> <span className="text-xs">(All Products Combined)</span>
                                </div>
                                <HeaderSampleSize
                                    draggableColumns={draggableColumns}
                                    productColumnVisibility={productColumnVisibility}
                                    isProductHeader={false}
                                />
                            </>
                        }
                        cellType="th"
                        testLabel=""
                        mainProps={{
                            colSpan: isBeautyAndWellbeing ? 3 : (box ? boxAnalysisTypeSelection.length : 1),
                            align: "center",
                        }}
                        styles={{
                            main: {
                                minWidth: 200,
                                maxWidth: 200,
                                zIndex: 2,
                            },
                            wrapper: {},
                            value: {
                                justifyContent: "space-between",
                                flexDirection: "column",
                            },
                        }}
                    />
                )}
                {overallToggle && !showConceptAndRecallData && !["claims"].includes(moduleName) && (
                    <TableCellWithOuterSeparator
                        value={
                            <>
                                <div style={{ textAlign: "center" }}>
                                    {isITMDataShown && overallToggle && "Recall "}Overall Sample <br /> <span className="text-xs">(All Products Combined)</span>
                                </div>
                                <HeaderSampleSize
                                    draggableColumns={draggableColumns}
                                    productColumnVisibility={productColumnVisibility}
                                    isProductHeader={false}
                                />
                            </>
                        }
                        cellType="th"
                        testLabel=""
                        mainProps={{
                            colSpan: isBeautyAndWellbeing ? (penaltyToggle ? 5 : 3) : (box ? boxAnalysisTypeSelection.length : 1),
                            align: "center",
                        }}
                        styles={{
                            main: {
                                minWidth: 200,
                                zIndex: 2,
                            },
                            wrapper: {},
                            value: {
                                justifyContent: "space-between",
                                flexDirection: "column",
                            },
                        }}
                    />
                )}
                {draggableColumns.map((datum, index) => {
                    let productCodeTitle = datum.title;
                    const productCodeCoreDataId = datum.coreDataId;
                    const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);
                    let studyReferenceOrTestCode = selectedStudy?.find((study) => study.id === datum.coreDataId)?.study_reference__title;
                    const showWeightIcon = selectedStudy.length > 1 && selectedStudy?.find((study) => study.id === datum.coreDataId)?.weighted === true;
                    if (isOralCare) {
                        const [title, code] = getOralCareProductCodeTableHeader(datum, productsTestCodeMapData);
                        productCodeTitle = title;
                        studyReferenceOrTestCode = (
                            <>
                                {code} / {studyReferenceOrTestCode}
                            </>
                        );
                    }
                    return (
                        <TableCellWithOuterSeparator
                            key={index + 1 + datum.title + "pc"}
                            value={
                                targetColumnIndex === index && hasActuallyMoved ? (
                                    ""
                                ) : (
                                    <>
                                        {showWeightIcon && (
                                            <img
                                                alt="weightToggle"
                                                style={{
                                                    width: 14,
                                                    marginRight: 10,
                                                }}
                                                src={WeightToggleIcon}
                                            />
                                        )}
                                        <button
                                            className="clean-button color-blue"
                                            style={{
                                                fontWeight: 600,
                                                fontSize: "0.875rem",
                                            }}
                                            onClick={(e) =>
                                                handleProductClick(e, {
                                                    coreDataId: datum.coreDataId,
                                                    productCode: datum.title,
                                                })
                                            }
                                        >
                                            {productCodeTitle} / {studyReferenceOrTestCode}
                                        </button>

                                        <HeaderSampleSize productData={datum} />
                                    </>
                                )
                            }
                            cellType="th"
                            testLabel=""
                            mainProps={{
                                colSpan: tableHeadSpan + rowLevelTTColSpan,
                                draggable: true,
                                onDragStart: (e) => handleColumnDragStart(e, index),
                                onDragOver: handleDragOver,
                                onDrop: () => handleColumnDrop(index),
                                align: "center",
                                className: className,
                            }}
                            styles={{
                                main: {
                                    fontWeight: "600",
                                    cursor: "move",
                                    minWidth: targetColumnIndex === index && hasActuallyMoved ? draggedColumnDiamension.width : 200,
                                    maxWidth: targetColumnIndex === index && hasActuallyMoved ? draggedColumnDiamension.height : 300,
                                    background: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "",
                                    borderColor: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "",
                                },
                                wrapper: {
                                    justifyContent: "space-between",
                                },
                                value: {
                                    justifyContent: "space-between",
                                    flexDirection: "column",
                                    alignItems: "center",
                                },
                            }}
                        />
                    );
                })}
            </tr>
        );
    };

    const returnConsumerFilterRow = () => {
        return (
            <tr>
                {moduleName === "claims" ? (
                    <>
                        <th
                            key={"col-cf"}
                            scope="col"
                            style={{
                                minWidth: 300,
                                maxWidth: 300,
                                position: "sticky",
                                left: 0,
                                zIndex: 2,
                            }}
                        >
                            <ClaimsTerritoryFilter
                                claimsTerritoryTags={claimsTerritoryTags}
                                page={"claimsAnalysis"}
                            />
                            {claimsTerritorySelectedTags.length !== 0 && (
                                <div
                                    style={{
                                        margin: "10px",
                                    }}
                                >
                                    {claimsTerritorySelectedTags.map((tag, i) => {
                                        return (
                                            <span
                                                key={i + 1 + tag}
                                                style={{
                                                    backgroundColor: "#b8c7ed",
                                                    borderRadius: "5px",
                                                    padding: "0px 3px",
                                                    display: "inline-block",
                                                    margin: "3px 2px",
                                                    fontSize: "8px",
                                                    letterSpacing: "1px",
                                                }}
                                            >
                                                {tag}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </th>
                        <th
                            key={"col-txt"}
                            scope="col"
                            style={{
                                minWidth: 300,
                                maxWidth: 500,
                                position: "sticky",
                                left: 300,
                                zIndex: 2,
                            }}
                        >
                            <TextField
                                fullWidth
                                placeholder="Search"
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    style: {
                                        height: "30px",
                                        fontSize: "14px",
                                    },
                                }}
                                onChange={(e) => setSearchValue(e.target.value)}
                            />
                        </th>
                    </>
                ) : (
                    stickyColumns.map((col, i) => {
                        return (
                            <th
                                key={col + i + "sticky-col"}
                                scope="col"
                                style={{
                                    minWidth: 200,
                                    maxWidth: 200,
                                    position: "sticky",
                                    left: i * 200,
                                    zIndex: 2,
                                }}
                            ></th>
                        );
                    })
                )}
                {box && <td colSpan={boxAnalysisTypeSelection.length}></td>}
                {showConceptAndRecallData && overallToggle && !["claims"].includes(moduleName) && (
                    <>
                        <td colSpan={box ? boxAnalysisTypeSelection.length : 1}></td>
                        <td colSpan={box ? boxAnalysisTypeSelection.length : 1}></td>
                    </>
                )}
                {isITMDataShown && overallToggle && !showConceptAndRecallData && !["claims"].includes(moduleName) && <td colSpan={isBeautyAndWellbeing ? 3 : (box ? boxAnalysisTypeSelection.length : 1)}></td>}
                {overallToggle && !showConceptAndRecallData && !["claims"].includes(moduleName) && <td colSpan={isBeautyAndWellbeing ? (penaltyToggle ? 5 : 3) : (box ? boxAnalysisTypeSelection.length : 1)}></td>}
                {draggableColumns.map((datum, index) => {
                    const productCodeTitle = datum.title;
                    const productCodeCoreDataId = datum.coreDataId;
                    const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);
                    return (
                        <td
                            key={index + 1 + "cf"}
                            colSpan={tableHeadSpan + rowLevelTTColSpan}
                            className={className}
                            style={{
                                textAlign: "center",
                                fontWeight: "500",
                                padding: "6px 0px",
                                background: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "",
                                borderColor: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "#fff",
                            }}
                        >
                            {targetColumnIndex === index && hasActuallyMoved ? (
                                ""
                            ) : (
                                <>
                                    {!isConsumerFilterLoadingStoreData ? (
                                        <ConsumerFilter
                                            productsSecondaryData={draggableColumns}
                                            product={datum}
                                            handleFilterSubmit={handleFilterSubmit}
                                        />
                                    ) : (
                                        <CircularProgress size={20} />
                                    )}
                                </>
                            )}
                        </td>
                    );
                })}
            </tr>
        );
    };

    const returnItmRecallLabelRow = () => {
        return (
            <tr>
                {stickyColumns.map((col, i) => {
                    return (
                        <th
                            key={col + i + "itm-recall"}
                            scope="col"
                            style={{
                                minWidth: 200,
                                maxWidth: 200,
                                position: "sticky",
                                left: i * 200,
                                zIndex: 2,
                            }}
                        ></th>
                    );
                })}
                {box && <td colSpan={boxAnalysisTypeSelection.length}></td>}
                {isITMDataShown && overallToggle && !showConceptAndRecallData && !["claims"].includes(moduleName) && <td colSpan={box ? dataViewKey.split(",").length : 1}></td>}
                {overallToggle && !showConceptAndRecallData && !["claims"].includes(moduleName) && <td colSpan={box ? dataViewKey.split(",").length : 1}></td>}
                {draggableColumns.map((datum, index) => {
                    const productCodeTitle = datum.title;
                    const productCodeCoreDataId = datum.coreDataId;
                    const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);

                    const key = index + 1 + productCodeTitle;
                    return (
                        <>
                            {isITMDataShown && (
                                <>
                                    {hasITMTextTheme && (
                                        <TableCellWithOuterSeparator
                                            key={key + "itm-tt"}
                                            value={targetColumnIndex === index && hasActuallyMoved ? "" : TTIconWithTooltip}
                                            cellType="td"
                                            testLabel=""
                                            mainProps={{
                                                className: `${className}`,
                                                colSpan: 1,
                                                draggable: true,
                                                onDragStart: (e) => handleColumnDragStart(e, index),
                                                onDragOver: handleDragOver,
                                                onDrop: () => handleColumnDrop(index),
                                            }}
                                            styles={textThemeHeaderStyles}
                                            showSeperator={true}
                                        />
                                    )}
                                    <TableCellWithOuterSeparator
                                        key={key + "itm"}
                                        value={targetColumnIndex === index && hasActuallyMoved ? "" : "ITM"}
                                        cellType="td"
                                        testLabel=""
                                        mainProps={{
                                            className: `${className}`,
                                            colSpan: isITMDataShown && !box ? 1 : dataViewKey.split(",").length,
                                            draggable: true,
                                            onDragStart: (e) => handleColumnDragStart(e, index),
                                            onDragOver: handleDragOver,
                                            onDrop: () => handleColumnDrop(index),
                                        }}
                                        styles={{
                                            main: {
                                                fontWeight: "600",
                                                cursor: "pointer",
                                                background: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "",
                                                minWidth: 120,
                                                maxWidth: 200,
                                            },
                                            wrapper: {},
                                            value: {
                                                justifyContent: "center",
                                                color: "#005eef",
                                                background: "#f0f8ff",
                                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                fontWeight: "600",
                                                borderRadius: "5px",
                                                padding: "5px",
                                            },
                                        }}
                                        showSeperator={true}
                                    />
                                </>
                            )}

                            {hasRecallTextTheme && (
                                <TableCellWithOuterSeparator
                                    key={key + "recall-tt"}
                                    value={targetColumnIndex === index && hasActuallyMoved ? "" : TTIconWithTooltip}
                                    cellType="td"
                                    testLabel=""
                                    mainProps={{
                                        className: `${className}`,
                                        colSpan: 1,
                                        draggable: true,
                                        onDragStart: (e) => handleColumnDragStart(e, index),
                                        onDragOver: handleDragOver,
                                        onDrop: () => handleColumnDrop(index),
                                    }}
                                    styles={textThemeHeaderStyles}
                                    showSeperator={true}
                                />
                            )}
                            <TableCellWithOuterSeparator
                                key={key + "recall-mean"}
                                value={targetColumnIndex === index && hasActuallyMoved ? "" : "Recall"}
                                cellType="td"
                                testLabel=""
                                mainProps={{
                                    className: `${className}`,
                                    colSpan: dataViewKey.split(",").length,
                                    draggable: true,
                                    onDragStart: (e) => handleColumnDragStart(e, index),
                                    onDragOver: handleDragOver,
                                    onDrop: () => handleColumnDrop(index),
                                }}
                                styles={{
                                    main: {
                                        fontWeight: "600",
                                        cursor: "pointer",
                                        background: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "",
                                        minWidth: 120,
                                        maxWidth: 200,
                                    },
                                    wrapper: {},
                                    value: {
                                        justifyContent: "center",
                                        color: "#005eef",
                                        background: "#f0f8ff",
                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                        fontWeight: "600",
                                        borderRadius: "5px",
                                        padding: "5px",
                                    },
                                }}
                                showSeperator={true}
                            />
                        </>
                    );
                })}
            </tr>
        );
    };

    // ==================== CONCEPT/RECALL LABEL ROW ====================
    const returnConceptRecallLabelRow = () => {
        return (
            <tr>
                {stickyColumns.map((col, i) => {
                    return (
                        <th
                            key={col + i + "concept-recall"}
                            scope="col"
                            style={{
                                minWidth: 200,
                                maxWidth: 200,
                                position: "sticky",
                                left: i * 200,
                                zIndex: 2,
                            }}
                        ></th>
                    );
                })}
                {box && <td colSpan={boxAnalysisTypeSelection.length}></td>}
                {isITMDataShown && overallToggle && !showConceptAndRecallData && !["claims"].includes(moduleName) && <td colSpan={box ? dataViewKey.split(",").length : 1}></td>}
                {overallToggle && !showConceptAndRecallData && !["claims"].includes(moduleName) && <td colSpan={box ? dataViewKey.split(",").length : 1}></td>}
                {showConceptAndRecallData && overallToggle && (
                    <>
                        <td colSpan={box ? boxAnalysisTypeSelection.length : 1}></td>
                        <td colSpan={box ? boxAnalysisTypeSelection.length : 1}></td>
                    </>
                )}
                {draggableColumns.map((datum, index) => {
                    const productCodeTitle = datum.title;
                    const productCodeCoreDataId = datum.coreDataId;
                    const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);
                    const key = index + 1 + productCodeTitle;
                    return (
                        <>
                            <TableCellWithOuterSeparator
                                key={key + "concept"}
                                value={targetColumnIndex === index && hasActuallyMoved ? "" : "Concept"}
                                cellType="td"
                                testLabel=""
                                mainProps={{
                                    className: `${className}`,
                                    colSpan: dataViewKey.split(",").length,
                                    draggable: true,
                                    onDragStart: (e) => handleColumnDragStart(e, index),
                                    onDragOver: handleDragOver,
                                    onDrop: () => handleColumnDrop(index),
                                }}
                                styles={{
                                    main: {
                                        fontWeight: "600",
                                        cursor: "pointer",
                                        background: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "",
                                        minWidth: 120,
                                        maxWidth: 200,
                                    },
                                    wrapper: {},
                                    value: {
                                        justifyContent: "center",
                                        color: "#005eef",
                                        background: "#f0f8ff",
                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                        fontWeight: "600",
                                        borderRadius: "5px",
                                        padding: "5px",
                                    },
                                }}
                                showSeperator={true}
                            />
                            <TableCellWithOuterSeparator
                                key={key + "recall-label"}
                                value={targetColumnIndex === index && hasActuallyMoved ? "" : "Recall"}
                                cellType="td"
                                testLabel=""
                                mainProps={{
                                    className: `${className}`,
                                    colSpan: dataViewKey.split(",").length,
                                    draggable: true,
                                    onDragStart: (e) => handleColumnDragStart(e, index),
                                    onDragOver: handleDragOver,
                                    onDrop: () => handleColumnDrop(index),
                                }}
                                styles={{
                                    main: {
                                        fontWeight: "600",
                                        cursor: "pointer",
                                        background: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "",
                                        minWidth: 120,
                                        maxWidth: 200,
                                    },
                                    wrapper: {},
                                    value: {
                                        justifyContent: "center",
                                        color: "#005eef",
                                        background: "#f0f8ff",
                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                        fontWeight: "600",
                                        borderRadius: "5px",
                                        padding: "5px",
                                    },
                                }}
                                showSeperator={false}
                            />
                        </>
                    );
                })}
            </tr>
        );
    };

    const returnTextThemeLabelRow = () => {
        return (
            <tr>
                {stickyColumns.map((col, i) => {
                    const isFirst = i === 0;
                    const minWid = isFirst ? (moduleName === "claims" ? 300 : 200) : 300;
                    const maxWid = moduleName === "claims" ? (isFirst ? 300 : 500) : isFirst ? 200 : 300;
                    const leftSpace = isFirst ? 0 : moduleName === "claims" ? 300 : 200;
                    return (
                        <th
                            key={col + i + "tt-sticky"}
                            scope="col"
                            style={{ minWidth: minWid, maxWidth: maxWid, position: "sticky", left: leftSpace, zIndex: 2 }}
                        ></th>
                    );
                })}
                {overallToggle && !["claims"].includes(moduleName) && <td colSpan={1}></td>}
                {draggableColumns.map((datum, index) => {
                    const productCodeTitle = datum.title;
                    const productCodeCoreDataId = datum.coreDataId;
                    const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);
                    return (
                        <>
                            {(hasRecallTextTheme || hasStandardTextTheme) && (
                                <TableCellWithOuterSeparator
                                    key={"tt-icon-" + index}
                                    value={targetColumnIndex === index && hasActuallyMoved && !(hasRecallTextTheme || hasStandardTextTheme) ? "" : TTIconWithTooltip}
                                    cellType="td"
                                    testLabel=""
                                    mainProps={{
                                        className,
                                        draggable: true,
                                        onDragStart: (e) => handleColumnDragStart(e, index),
                                        onDragOver: handleDragOver,
                                        onDrop: () => handleColumnDrop(index),
                                    }}
                                    styles={textThemeHeaderStyles}
                                    showSeperator={true}
                                />
                            )}
                            <TableCellWithOuterSeparator
                                key={"tt-empty-" + index}
                                value={""}
                                cellType="td"
                                testLabel=""
                                mainProps={{
                                    className, colSpan: dataViewKey.split(",").length,
                                    draggable: true,
                                    onDragStart: (e) => handleColumnDragStart(e, index),
                                    onDragOver: handleDragOver,
                                    onDrop: () => handleColumnDrop(index),
                                }}
                                styles={{
                                    main: { background: targetColumnIndex === index && hasActuallyMoved ? "#ffffff" : "" },
                                    wrapper: {},
                                    value: { background: "transparent", border: "none", height: "32px" }
                                }}
                                showSeperator={true}
                            />
                        </>
                    );
                })}
            </tr>
        );
    };

    const getRowHasITMTT = (itmBvObj) => {
        if (!enableTextThemeAnalysis || !isITMDataShown) return false;
        if (moduleName !== "overall" || !itmBvObj?.backendVariable) return false;
        return draggableColumns.some(datum => {
            const scoreObj = datum.scores?.find(s => s.backendVariable === itmBvObj.backendVariable);
            return scoreObj?.text_theme_check_box === true;
        });
    };

    const getRowHasRecallTT = (recallBvObj) => {
        if (!enableTextThemeAnalysis || !isITMDataShown) return false;
        if (moduleName !== "overall" || !recallBvObj?.backendVariable) return false;
        return draggableColumns.some(datum => {
            const scoreObj = datum.scores?.find(s => s.backendVariable === recallBvObj.backendVariable);
            return scoreObj?.text_theme_check_box === true;
        });
    };

    const getRowHasStandardTT = (filteredBvObjScoreForTT) => {
        if (!enableTextThemeAnalysis || isITMDataShown) return false;
        if (!filteredBvObjScoreForTT?.backendVariable) return false;
        return draggableColumns.some(datum => {
            const scoreObj = datum.scores?.find(s => s.backendVariable === filteredBvObjScoreForTT.backendVariable);
            return scoreObj?.text_theme_check_box === true;
        });
    };

    const getPerProductCheckbox = (cellIndex, type, bvObj) => {
        const datum = draggableColumns[cellIndex];
        if (!datum || !datum.scores) return false;
        const backendVar = bvObj?.backendVariable || "";
        if (!backendVar) return false;
        const scoreObj = datum.scores.find(s => s.backendVariable === backendVar);
        return scoreObj?.text_theme_check_box === true;
    };

    const handleTextThemeCheckBoxOnchange = (e, openEndedBackendVariable, productCoreDataId, productTitle) => {
        const tempSelection = [...textThemeSelections];

        const isPresent = tempSelection.some((ele) => ele.backendVariable === openEndedBackendVariable && ele.coreDataId === productCoreDataId && ele.productCodeTitle === productTitle);
        let finalSelection = [];

        if (isPresent) {
            finalSelection = tempSelection.map((datum) => {
                if (datum.backendVariable === openEndedBackendVariable && datum.coreDataId === productCoreDataId && datum.productCodeTitle === productTitle) {
                    return {
                        backendVariable: openEndedBackendVariable,
                        coreDataId: productCoreDataId,
                        productCodeTitle: productTitle,
                        isSelected: e.target.checked,
                    };
                } else {
                    return datum;
                }
            });
        } else {
            finalSelection = [
                ...tempSelection,
                {
                    backendVariable: openEndedBackendVariable,
                    coreDataId: productCoreDataId,
                    productCodeTitle: productTitle,
                    isSelected: e.target.checked,
                },
            ];
        }
        dispatch(toggleTextThemeSelection(finalSelection));
    };


    const renderTTCell = (showTT, cellIndex, type = "", bvObj, sampleSize, openEndedBackendVariable) => {
        const _className = getProductColumnClassName(
            draggableColumns[cellIndex]?.title || "",
            draggableColumns[cellIndex]?.coreDataId || "",
            productColumnVisibility
        );

        const shouldShowCheckbox = getPerProductCheckbox(cellIndex, type, bvObj);

        const productCoreDataId = draggableColumns[cellIndex]?.coreDataId || cellIndex;
        const productTitle = draggableColumns[cellIndex]?.title?.replace(/\s+/g, "_") || cellIndex;
        // const backendVarForKey = type === "itm"
        //     ? (itmBvObj?.backendVariable || "itm")
        //     : type === "recall"
        //         ? (recallBvObj?.backendVariable || "recall")
        //         : (filteredBvObjScoreForTT?.backendVariable || "std");
        // const backendVarForKey = bvObj?.backendVariable || type;
        const checkboxKey = `${productCoreDataId}-${productTitle}-${openEndedBackendVariable}`;
        const isChecked = textThemeSelections.some((ele) => ele.backendVariable === openEndedBackendVariable && ele.coreDataId === productCoreDataId && ele.productCodeTitle === productTitle && ele.isSelected);

        return (
            <TableCellWithOuterSeparator
                key={checkboxKey}
                value={
                    shouldShowCheckbox ? (
                        <Checkbox
                            size="small"
                            checked={isChecked}
                            disabled={sampleSize <= 10}
                            onChange={(e) => handleTextThemeCheckBoxOnchange(e, openEndedBackendVariable, productCoreDataId, productTitle)}
                            sx={{
                                padding: 0,
                                color: "#667085",
                                backgroundColor: sampleSize <= 10 ? "#c8c8c8" : "transparent",
                                "&.Mui-checked": {
                                    color: "#005eef",
                                },
                                "& .MuiSvgIcon-root": {
                                    fontSize: 16,
                                },
                            }}
                        />
                    ) : (
                        <div
                            style={{
                                width: "16px",
                                height: "16px",
                            }}
                        />
                    )
                }
                cellType="td"
                testLabel=""
                mainProps={{ className: _className }}
                styles={{
                    main: {
                        minWidth: 36,
                        maxWidth: 36,
                        width: 36,
                        padding: "4px 2px",
                        backgroundColor: targetColumnIndex === cellIndex && hasActuallyMoved
                            ? "#d8eeff"
                            : "transparent",
                    },
                    wrapper: {
                        justifyContent: "center",
                        alignItems: "center",
                        padding: 0,
                        height: "100%",
                        width: "100%",
                        display: "flex",
                    },
                    value: {
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        paddingLeft: "8px",
                        background: "#fff",
                        border: "1px solid #ffffff",
                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.36)",
                        borderRadius: "5px",
                        width: "26px",
                        height: "26px",
                    }
                }}
                showSeperator={true}
            />
        );
    };
    const returnBoxAnalysisLabelRow = () => {
        return (
            <tr>
                {stickyColumns.map((col, i) => {
                    const isFirst = i === 0;
                    const minWid = isFirst ? (moduleName === "claims" ? 300 : 200) : 300;
                    const maxWid = moduleName === "claims" ? (isFirst ? 300 : 500) : isFirst ? 200 : 300;
                    const leftSpace = isFirst ? 0 : moduleName === "claims" ? 300 : 200;
                    return (
                        <th
                            key={col + i + "box"}
                            scope="col"
                            style={{
                                minWidth: minWid,
                                maxWidth: maxWid,
                                position: "sticky",
                                left: leftSpace,
                                zIndex: 2,
                            }}
                        ></th>
                    );
                })}
                {boxAnalysisTypeSelection.map((ele, i) => {
                    const key = "box-analysis-label-column-" + i;
                    return (
                        <TableCellWithOuterSeparator
                            key={key}
                            value={ele}
                            cellType="td"
                            testLabel=""
                            styles={{
                                main: {},
                                wrapper: {},
                                value: {
                                    justifyContent: "center",
                                    color: "#005eef",
                                    background: "#f0f8ff",
                                    boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                    fontWeight: "600",
                                    borderRadius: "5px",
                                    padding: "5px",
                                },
                            }}
                            showSeperator={ele === "Bottom Box" ? false : true}
                        />
                    );
                })}
                {isITMDataShown &&
                    overallToggle &&
                    !showConceptAndRecallData &&
                    !["claims"].includes(moduleName) &&
                    boxAnalysisTypeSelection.map((ele, i) => {
                        const key = "box-analysis-itm-label-column-" + i;
                        return (
                            <TableCellWithOuterSeparator
                                key={key}
                                value={ele}
                                cellType="td"
                                testLabel=""
                                styles={{
                                    main: {},
                                    wrapper: {},
                                    value: {
                                        justifyContent: "center",
                                        color: "#005eef",
                                        background: "#f0f8ff",
                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                        fontWeight: "600",
                                        borderRadius: "5px",
                                        padding: "5px",
                                    },
                                }}
                                showSeperator={ele === "Bottom Box" ? false : true}
                            />
                        );
                    })}
                {showConceptAndRecallData && overallToggle && !["claims"].includes(moduleName) && (
                    <>
                        {/* Concept Overall - Top Box / Bottom Box */}
                        {boxAnalysisTypeSelection.map((ele, i) => (
                            <TableCellWithOuterSeparator
                                key={`concept-overall-box-${i}`}
                                value={ele}
                                cellType="td"
                                testLabel=""
                                styles={{
                                    main: {},
                                    wrapper: {},
                                    value: {
                                        justifyContent: "center",
                                        color: "#005eef",
                                        background: "#f0f8ff",
                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                        fontWeight: "600",
                                        borderRadius: "5px",
                                        padding: "5px",
                                    },
                                }}
                                showSeperator={ele === "Bottom Box" ? false : true}
                            />
                        ))}

                        {/* Recall Overall - Top Box / Bottom Box */}
                        {boxAnalysisTypeSelection.map((ele, i) => (
                            <TableCellWithOuterSeparator
                                key={`recall-overall-box-${i}`}
                                value={ele}
                                cellType="td"
                                testLabel=""
                                styles={{
                                    main: {},
                                    wrapper: {},
                                    value: {
                                        justifyContent: "center",
                                        color: "#005eef",
                                        background: "#f0f8ff",
                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                        fontWeight: "600",
                                        borderRadius: "5px",
                                        padding: "5px",
                                    },
                                }}
                                showSeperator={ele === "Bottom Box" ? false : true}
                            />
                        ))}
                    </>
                )}
                {overallToggle &&
                    !showConceptAndRecallData &&
                    !["claims"].includes(moduleName) &&
                    boxAnalysisTypeSelection.map((ele, i) => {
                        const key = "box-analysis-recall-label-column-" + i;
                        return (
                            <TableCellWithOuterSeparator
                                key={key}
                                value={ele}
                                cellType="td"
                                testLabel=""
                                styles={{
                                    main: {},
                                    wrapper: {},
                                    value: {
                                        justifyContent: "center",
                                        color: "#005eef",
                                        background: "#f0f8ff",
                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                        fontWeight: "600",
                                        borderRadius: "5px",
                                        padding: "5px",
                                    },
                                }}
                                showSeperator={ele === "Bottom Box" ? false : true}
                            />
                        );
                    })}
                {box &&
                    draggableColumns.map((datum, index) => {
                        const productCodeTitle = datum.title;
                        const productCodeCoreDataId = datum.coreDataId;
                        const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);
                        const key = index + 1 + productCodeTitle;
                        return (
                            <>
                                {/* ITM Box labels - only when ITM is shown and NOT in Concept+Recall mode */}
                                {isITMDataShown && hasITMTextTheme && (
                                    <TableCellWithOuterSeparator
                                        key={key + "itm-tt"}
                                        value={targetColumnIndex === index && hasActuallyMoved ? "" : box ? "" : TTIconWithTooltip}
                                        cellType="td"
                                        testLabel=""
                                        mainProps={{
                                            className: `${className}`,
                                            colSpan: 1,
                                            draggable: true,
                                            onDragStart: (e) => handleColumnDragStart(e, index),
                                            onDragOver: handleDragOver,
                                            onDrop: () => handleColumnDrop(index),
                                        }}
                                        styles={textThemeHeaderStyles}
                                        showSeperator={true}
                                    />
                                )}
                                {isITMDataShown &&
                                    !showConceptAndRecallData &&
                                    boxAnalysisTypeSelection.map((ele, i) => {
                                        const key = "box-analysis-itm-product-label-column-" + index + "-" + i;
                                        return (
                                            <TableCellWithOuterSeparator
                                                key={key}
                                                value={targetColumnIndex === index && hasActuallyMoved ? "" : ele}
                                                cellType="td"
                                                testLabel=""
                                                mainProps={{
                                                    className: className,
                                                }}
                                                styles={{
                                                    main: {
                                                        fontWeight: "600",
                                                        cursor: "pointer",
                                                        background: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "",
                                                    },
                                                    wrapper: {},
                                                    value: {
                                                        justifyContent: "center",
                                                        color: "#005eef",
                                                        background: "#f0f8ff",
                                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                        fontWeight: "600",
                                                        borderRadius: "5px",
                                                        padding: "5px",
                                                    },
                                                }}
                                                showSeperator={ele === "Bottom Box" ? false : true}
                                            />
                                        );
                                    })}

                                {(moduleName === "overall" ? hasRecallTextTheme : hasStandardTextTheme) && (
                                    <TableCellWithOuterSeparator
                                        key={key + "recall-tt"}
                                        value={targetColumnIndex === index && hasActuallyMoved ? "" : TTIconWithTooltip}
                                        cellType="td"
                                        testLabel=""
                                        mainProps={{
                                            className: `${className}`,
                                            colSpan: 1,
                                            draggable: true,
                                            onDragStart: (e) => handleColumnDragStart(e, index),
                                            onDragOver: handleDragOver,
                                            onDrop: () => handleColumnDrop(index),
                                        }}
                                        styles={textThemeHeaderStyles}
                                        showSeperator={true}
                                    />

                                    // )}: (
                                    //     <TableCellWithOuterSeparator
                                    //         key={key + "recall-tt"}
                                    //         value={""}
                                    //         cellType="td"
                                    //         testLabel=""
                                    //         mainProps={{
                                    //             className: `${className}`,
                                    //             colSpan: 1,
                                    //             draggable: true,
                                    //             onDragStart: (e) => handleColumnDragStart(e, index),
                                    //             onDragOver: handleDragOver,
                                    //             onDrop: () => handleColumnDrop(index),
                                    //         }}
                                    //         styles={textThemeHeaderStyles}
                                    //         showSeperator={true}
                                    //     /> 
                                )}
                                {/* Box labels - ALWAYS show for ALL modes */}
                                {showConceptAndRecallData
                                    ? ["Concept", "Recall"].map((type) =>
                                        boxAnalysisTypeSelection.map((ele, i) => {
                                            const key = `box-analysis-${type.toLowerCase()}-${index}-${i}`;

                                            return (
                                                <TableCellWithOuterSeparator
                                                    key={key}
                                                    value={targetColumnIndex === index && hasActuallyMoved ? "" : ele}
                                                    cellType="td"
                                                    testLabel=""
                                                    mainProps={{
                                                        className: className,
                                                    }}
                                                    styles={{
                                                        main: {
                                                            fontWeight: "600",
                                                            cursor: "pointer",
                                                            background: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "",
                                                        },
                                                        wrapper: {},
                                                        value: {
                                                            justifyContent: "center",
                                                            color: "#005eef",
                                                            background: "#f0f8ff",
                                                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                            fontWeight: "600",
                                                            borderRadius: "5px",
                                                            padding: "5px",
                                                        },
                                                    }}
                                                    showSeperator={type === "Concept" ? (ele === "Bottom Box" ? false : true) : false}
                                                />
                                            );
                                        })
                                    )
                                    : boxAnalysisTypeSelection.map((ele, i) => {
                                        const key = "box-analysis-recall-product-label-column-" + index + "-" + i;
                                        const label = ele;
                                        return (
                                            <TableCellWithOuterSeparator
                                                key={key}
                                                value={targetColumnIndex === index && hasActuallyMoved ? "" : label}
                                                cellType="td"
                                                testLabel=""
                                                mainProps={{
                                                    className: className,
                                                }}
                                                styles={{
                                                    main: {
                                                        fontWeight: "600",
                                                        cursor: "pointer",
                                                        background: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "",
                                                    },
                                                    wrapper: {},
                                                    value: {
                                                        justifyContent: "center",
                                                        color: "#005eef",
                                                        background: "#f0f8ff",
                                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                        fontWeight: "600",
                                                        borderRadius: "5px",
                                                        padding: "5px",
                                                    },
                                                }}
                                                showSeperator={ele === "Bottom Box" ? false : isITMDataShown ? false : true}
                                            />
                                        );
                                    })}
                            </>
                        );
                    })}
            </tr>
        );
    };

    const returnAnalysisScoresRow = () => {
        const [key1, key2, key3] = getViewKeys(dataViewKey);
        const conceptCategoryByModule = {
            overall: "Concept KPI (OO)",
            agreement: "Concept Agreement KPI",
            secondary: "Concept Secondary Attribute",
        };
        const recallCategoryByModule = {
            overall: "KPI (OO)",
            agreement: "Agreement KPI",
            secondary: "Secondary Attribute",
        };

        const allMappingsBvs = showConceptAndRecallData
            ? tableDataMapping.flatMap((m) => m.displayLabels || [])
            : [];

        const _roww = tableDataMapping.map((catObj, i) => {
            const allKpis = catObj?.displayLabels || [];
            let selectedKpis = allKpis;
            if (showConceptData && conceptCategoryByModule[moduleName]) {
                selectedKpis = allKpis.filter((kpi) => kpi.variableCategory === conceptCategoryByModule[moduleName]);
            } else if (showRecallData && recallCategoryByModule[moduleName]) {
                selectedKpis = allKpis.filter((kpi) => kpi.variableCategory === recallCategoryByModule[moduleName]);
            }
            const tableCatObj = {
                ...catObj,
                displayLabels: selectedKpis,
            };
            const scoreCatObj = isHairCare && showConceptAndRecallData ? catObj : tableCatObj;
            const kpis = tableCatObj.displayLabels || [];
            const { rowSpan, productScoreWithNaObj } = getProductScoreWithNaAndRowSpan({ catObj: scoreCatObj, box, key1, key2, key3, isITMDataShown, moduleName, draggableColumns, noOfProducts, removedKpiStoreState, isConceptRecallShown, isHairCare, isConceptDataShown: showConceptData });
            const groductScoreSumObject = getProductScoreSumObject({ catObj: scoreCatObj, box, key1, key2, key3, isITMDataShown, moduleName, draggableColumns, productColumnVisibility, isConceptRecallShown });
            let ifAvailableFirstRow = true;
            const uniqueKpis = new Set();
            const _trs = kpis
                .map((bvObj) => {
                    const rowDisplayLabel = bvObj.displayLabel;
                    const rowBackendVariable = bvObj.backendVariable;
                    let firstColumn = true;
                    if (!uniqueKpis.has(bvObj.displayLabel)) {
                        if (!ifAvailableFirstRow && catObj.displayLabels?.length >= 2) {
                            firstColumn = false;
                        }
                        const { recallBvObj, itmBvObj } = getRecallAndITM(rowDisplayLabel, rowBackendVariable, moduleName, kpis);

                        // const filteredBvObjScoreForTT = kpis.filter((ele) => ele.displayLabel === rowDisplayLabel)?.[0] || {};

                        const rowHasITMTT = getRowHasITMTT(itmBvObj);
                        const rowHasRecallTT = getRowHasRecallTT(recallBvObj);
                        const filteredBvObjScoreForTT = moduleName !== "overall"
                            ? (kpis.filter((ele) => ele.displayLabel === rowDisplayLabel)?.[0] || {})
                            : {};
                        const rowHasStandardTT = getRowHasStandardTT(filteredBvObjScoreForTT);
                        const productsScoreWithNA = productScoreWithNaObj[rowDisplayLabel];
                        const { itmTopOrMeanKey1, itmbottomKey2, recallTopOrMeanKey1, recallbottomKey2, recallThirdKey3, conceptTopOrMeanKey1, conceptbottomKey2, conceptThirdKey3, topPenaltyAvg, bottomPenaltyAvg } = groductScoreSumObject[rowDisplayLabel];
                        const rowMacroCategoryLabel = catObj.macroCategory.charAt(0).toUpperCase() + catObj.macroCategory.slice(1);
                        let rowBackendVariableDisplayLabel = bvObj.displayLabel.charAt(0).toUpperCase() + bvObj.displayLabel.slice(1);
                        if (likertScaleData?.[bvObj.backendVariable] || (likertScaleData?.[bvObj.backendVariable] == 0)) {
                            rowBackendVariableDisplayLabel += ` (${likertScaleData[bvObj.backendVariable]})`;
                        }
                        const showActionStandard = !((isSkinCare && role?.toLowerCase() === "user")) && (actionStandardKpis?.includes(recallBvObj?.backendVariable) || actionStandardKpis?.includes(itmBvObj?.backendVariable)) && (selectedStudy.length === 1 || isITMDataShown);
                        if (showActionStandard) {
                            rowBackendVariableDisplayLabel += "*";
                        }
                        const label = highlightText(rowBackendVariableDisplayLabel, searchValue);
                        const { conceptBvObj: rowConceptBvObj } = getConceptAndRecall(rowDisplayLabel, rowBackendVariable, moduleName, kpis);
                        const boxAnalysisSelectionKey =
                            moduleName === "overall" && Object.keys(recallBvObj).length === 0
                                ? (itmBvObj?.backendVariable || rowConceptBvObj?.backendVariable)
                                : (recallBvObj?.backendVariable || rowConceptBvObj?.backendVariable);
                        const selectionFilteredData = boxAnalysisSelection[boxAnalysisSelectionKey];

                        const row = (
                            <tr
                                key={i + 1 + "row" + rowBackendVariable}
                                className="score-row"
                            >
                                {firstColumn && (moduleName === "overall" || moduleName === "claims" || moduleName === "fragranceMetrics") && (
                                    <TableCellWithInnerSeparator
                                        key={catObj.macroCategory + "first-co" + i}
                                        value={
                                            moduleName === "claims" ? (
                                                <div
                                                    style={{
                                                        wordWrap: "break-word",
                                                        whiteSpace: "normal",
                                                    }}
                                                >
                                                    {rowMacroCategoryLabel.split(",").map((tag, i) => {
                                                        const key = i + 1 + tag;
                                                        return (
                                                            <div
                                                                key={key}
                                                                style={{
                                                                    backgroundColor: "#D8E8FE",
                                                                    borderRadius: "5px",
                                                                    padding: "2px 6px",
                                                                    display: "inline-block",
                                                                    margin: "3px 6px",
                                                                }}
                                                            >
                                                                {tag}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                rowMacroCategoryLabel
                                            )
                                        }
                                        cellType="td"
                                        testLabel=""
                                        mainProps={{
                                            rowSpan: rowSpan,
                                        }}
                                        styles={{
                                            main: {
                                                minWidth: 200,
                                                maxWidth: moduleName === "claims" ? 300 : 200,
                                                left: 0,
                                                position: "sticky",
                                                zIndex: 2,
                                            },
                                            wrapper: {
                                                background: "#fff",
                                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                borderRadius: "5px",
                                                padding: "5px",
                                            },
                                            value: {
                                                alignItems: "center",
                                            },
                                        }}
                                        showSeperator={true}
                                    />
                                )}
                                {moduleName === "overall" || moduleName === "claims" || moduleName === "fragranceMetrics" ? (
                                    <TableCellWithOuterSeparator
                                        value={
                                            <>
                                                {showCheckBox && (
                                                    <RemoveRestoreBvCheckbox
                                                        backendVariableSettings={{
                                                            removedKpi: removedKpi,
                                                            backendVariable: bvObj.backendVariable,
                                                            setRemovedKpi: setRemovedKpi,
                                                            mappings: tableDataMapping,
                                                        }}
                                                    />
                                                )}
                                                {box && boxAnalysisTypeSelection.length > 1 && (
                                                    <OverlappingScales
                                                        topBoxSelection={selectionFilteredData?.top_box}
                                                        bottomBoxSelection={selectionFilteredData?.bottom_box}
                                                    />
                                                )}
                                                <span style={{ textAlign: "left" }}>
                                                    <span dangerouslySetInnerHTML={{ __html: label }} />
                                                </span>
                                            </>
                                        }
                                        cellType="td"
                                        testLabel=""
                                        styles={{
                                            main: {
                                                left: moduleName === "claims" ? 300 : 200,
                                                position: "sticky",
                                                fontWeight: showActionStandard ? 600 : 500, // Bold only for single study
                                                zIndex: 2,
                                            },
                                            wrapper: {},
                                            value: {
                                                background: "#fff",
                                                whiteSpace: "nowrap",
                                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                borderRadius: "5px",
                                                padding: "5px",
                                            },
                                        }}
                                        showSeperator={true}
                                    />
                                ) : (
                                    <TableCellWithInnerSeparator
                                        value={
                                            <>
                                                {showCheckBox && (
                                                    <RemoveRestoreBvCheckbox
                                                        backendVariableSettings={{
                                                            removedKpi: removedKpi,
                                                            backendVariable: bvObj.backendVariable,
                                                            setRemovedKpi: setRemovedKpi,
                                                            mappings: tableDataMapping,
                                                        }}
                                                    />
                                                )}
                                                {box && boxAnalysisTypeSelection.length > 1 && (
                                                    <OverlappingScales
                                                        topBoxSelection={selectionFilteredData?.top_box}
                                                        bottomBoxSelection={selectionFilteredData?.bottom_box}
                                                    />
                                                )}
                                                <span style={{ textAlign: "left" }}>
                                                    <span dangerouslySetInnerHTML={{ __html: label }} />
                                                </span>
                                            </>
                                        }
                                        cellType="td"
                                        testLabel=""
                                        styles={{
                                            main: {
                                                left: 0,
                                                position: "sticky",
                                                fontWeight: showActionStandard ? 600 : 500,
                                                zIndex: 2,
                                            },
                                            wrapper: {},
                                            value: {
                                                background: "#fff",
                                                whiteSpace: "nowrap",
                                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                borderRadius: "5px",
                                                padding: "5px",
                                            },
                                        }}
                                        showSeperator={true}
                                    />
                                )}
                                {box &&
                                    boxAnalysisTypeSelection.map((ele, i) => {
                                        const key = "box-analysis-selection-column-" + i;
                                        return (
                                            <TableCellWithOuterSeparator
                                                key={key}
                                                value={getSelectedOption(ele.split(" ")[0].toLowerCase(), selectionFilteredData)}
                                                cellType="td"
                                                testLabel=""
                                                styles={{
                                                    main: {},
                                                    wrapper: {},
                                                    value: {
                                                        justifyContent: "center",
                                                        background: "#fff",
                                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                        borderRadius: "5px",
                                                        padding: "5px",
                                                    },
                                                }}
                                                showSeperator={ele === "Bottom Box" ? false : true}
                                            />
                                        );
                                    })}
                                {isITMDataShown &&
                                    overallToggle &&
                                    !showConceptAndRecallData &&
                                    !["claims"].includes(moduleName) &&
                                    dataViewKey.split(",").map((ele, i) => {
                                        const key = "box-analysis-overall-itm-value-column-" + i;
                                        return (
                                            <TableCellWithOuterSeparator
                                                key={key}
                                                value={getOverallCalculationResult(i === 0 ? itmTopOrMeanKey1 : itmbottomKey2, totalSampleSize, totalVisibleProducts, ele)}
                                                cellType="td"
                                                testLabel=""
                                                styles={{
                                                    main: {},
                                                    wrapper: {},
                                                    value: {
                                                        justifyContent: "center",
                                                        background: "#fff",
                                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                        borderRadius: "5px",
                                                        padding: "5px",
                                                    },
                                                }}
                                                showSeperator={ele.startsWith("bottom") ? false : true}
                                            />
                                        );
                                    })}
                                {overallToggle && !showConceptAndRecallData && !["claims"].includes(moduleName) && (
                                    <>
                                        {isBeautyAndWellbeing &&
                                            penaltyToggle &&
                                            (() => {
                                                const leftPenalty = isSkinCare ? Math.abs(topPenaltyAvg) : topPenaltyAvg;
                                                const jrOL = totalSampleSize > 0 ? (recallbottomKey2 * 100) / totalSampleSize : 0;
                                                const isBlackStyle = jrOL >= 75;
                                                const getPenaltyColorL = (p) => {
                                                    if (jrOL >= 75) return "#000";
                                                    if (p >= 0.75) return "#E14370";
                                                    if (p >= 0.65) return "#D264F4";
                                                    return "#31A7FA";
                                                };

                                                return (
                                                    <TableCellWithOuterSeparator
                                                        key="overall-penalty-left-score"
                                                        value={!isNaN(leftPenalty) && leftPenalty !== undefined ? Number(leftPenalty).toFixed(2) : "NA"}
                                                        cellType="td"
                                                        testLabel=""
                                                        styles={{
                                                            main: {},
                                                            wrapper: {},
                                                            value: {
                                                                justifyContent: "center",
                                                                background: "#fff",
                                                                color: getPenaltyColorL(leftPenalty),
                                                                border: isBeautyAndWellbeing && !isBlackStyle ? `1px solid ${getPenaltyColorL(leftPenalty)}` : "none",
                                                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                                borderRadius: "5px", padding: "5px",
                                                                fontWeight: isBeautyAndWellbeing && !isBlackStyle ? "600" : "normal",
                                                            }
                                                        }}
                                                        showSeperator={true}
                                                    />
                                                );
                                            })()}
                                        {dataViewKey.split(",").map((ele, i) => {
                                            const key = "box-analysis-overall-recall-value-column-" + i;
                                            const _k1 = showConceptData ? conceptTopOrMeanKey1 : recallTopOrMeanKey1;
                                            const _k2 = showConceptData ? conceptbottomKey2 : recallbottomKey2;
                                            const _k3 = showConceptData ? conceptThirdKey3 : recallThirdKey3;
                                            const overallScoreSum = i === 0 ? _k1 : i === 1 ? _k2 : _k3;
                                            let displayValue;
                                            if (absoluteToggle && box) {
                                                displayValue = getFormattedScoreOrNA(overallScoreSum, ele.split(",")[0], absoluteToggle);
                                            } else {
                                                displayValue = getOverallCalculationResult(overallScoreSum, totalSampleSize, totalVisibleProducts, ele);
                                            }
                                            return (
                                                <TableCellWithOuterSeparator
                                                    key={key}
                                                    value={displayValue}
                                                    cellType="td"
                                                    testLabel=""
                                                    styles={{
                                                        main: {},
                                                        wrapper: {},
                                                        value: {
                                                            justifyContent: "center",
                                                            background: (() => {
                                                                if (isBeautyAndWellbeing && i === 1) {
                                                                    const jrO = totalSampleSize > 0 ? (recallbottomKey2 * 100) / totalSampleSize : 0;
                                                                    return jrO >= 75 ? "#008000" : "#FFBF00";
                                                                }
                                                                if (!penaltyToggle) return "#fff";
                                                                const jrO = totalSampleSize > 0 ? (recallbottomKey2 * 100) / totalSampleSize : 0;
                                                                if (jrO >= 75) {
                                                                    if (i === 1) return "#008000";
                                                                    return "#fff";
                                                                }
                                                                const tlO = totalSampleSize > 0 ? (recallTopOrMeanKey1 * 100) / totalSampleSize : 0;
                                                                const tmO = totalSampleSize > 0 ? (recallThirdKey3 * 100) / totalSampleSize : 0;
                                                                const diffO = Math.abs(tlO - tmO);
                                                                if (i === 1) return "#FFBF00";
                                                                if (i === 0 && tlO >= 20) return "#FF0000";
                                                                if (i === 2 && tmO >= 20) return "#FF0000";
                                                                return diffO >= 10 ? "#FF0000" : "#808080";
                                                            })(),
                                                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                            borderRadius: "5px",
                                                            padding: "5px",
                                                        },
                                                    }}
                                                    showSeperator={isBeautyAndWellbeing ? (penaltyToggle ? false : i === 0) : ele.startsWith("bottom") ? false : true}
                                                />
                                            );
                                        })}
                                        {isBeautyAndWellbeing &&
                                            penaltyToggle &&
                                            (() => {
                                                const rightPenalty = isSkinCare ? Math.abs(bottomPenaltyAvg) : bottomPenaltyAvg;
                                                const jrOR = totalSampleSize > 0 ? (recallbottomKey2 * 100) / totalSampleSize : 0;
                                                const isBlackStyle = jrOR >= 75;
                                                const getPenaltyColorR = (p) => {
                                                    if (jrOR >= 75) return "#000";
                                                    if (p >= 0.75) return "#E14370";
                                                    if (p >= 0.65) return "#D264F4";
                                                    return "#31A7FA";
                                                };

                                                return (
                                                    <TableCellWithOuterSeparator
                                                        key="overall-penalty-right-score"
                                                        value={!isNaN(rightPenalty) && rightPenalty !== undefined ? Number(rightPenalty).toFixed(2) : "NA"}
                                                        cellType="td"
                                                        testLabel=""
                                                        styles={{
                                                            main: {},
                                                            wrapper: {},
                                                            value: {
                                                                justifyContent: "center",
                                                                background: "#fff",
                                                                color: getPenaltyColorR(rightPenalty),
                                                                border: isBeautyAndWellbeing && !isBlackStyle ? `1px solid ${getPenaltyColorR(rightPenalty)}` : "none",
                                                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                                borderRadius: "5px", padding: "5px",
                                                                fontWeight: isBeautyAndWellbeing && !isBlackStyle ? "600" : "normal",
                                                            }
                                                        }}
                                                        showSeperator={false}
                                                    />
                                                );
                                            })()}
                                    </>
                                )}
                                {showConceptAndRecallData && overallToggle && (
                                    <>
                                        {/* Concept Overall */}
                                        {box ? (
                                            dataViewKey.split(",").map((ele, i) => {
                                                const conceptOverallSum = i === 0 ? conceptTopOrMeanKey1 : i === 1 ? conceptbottomKey2 : conceptThirdKey3;
                                                const conceptDisplayValue = absoluteToggle ? getFormattedScoreOrNA(conceptOverallSum, ele.split(",")[0], absoluteToggle) : getOverallCalculationResult(conceptOverallSum, totalSampleSize, totalVisibleProducts, ele);
                                                return (
                                                    <TableCellWithOuterSeparator
                                                        key={`concept-overall-${rowBackendVariable}-${i}`}
                                                        value={conceptDisplayValue}
                                                        cellType="td"
                                                        testLabel=""
                                                        styles={{
                                                            main: {},
                                                            wrapper: {},
                                                            value: {
                                                                justifyContent: "center",
                                                                background: "#fff",
                                                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                                borderRadius: "5px",
                                                                padding: "5px",
                                                            },
                                                        }}
                                                        showSeperator={ele.startsWith("bottom") ? false : true}
                                                    />
                                                );
                                            })
                                        ) : (
                                            <TableCellWithOuterSeparator
                                                key={`concept-overall-${rowBackendVariable}`}
                                                value={getOverallCalculationResult(conceptTopOrMeanKey1, totalSampleSize, totalVisibleProducts, dataViewKey)}
                                                cellType="td"
                                                testLabel=""
                                                styles={{
                                                    main: {},
                                                    wrapper: {},
                                                    value: {
                                                        justifyContent: "center",
                                                        background: "#fff",
                                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                        borderRadius: "5px",
                                                        padding: "5px",
                                                    },
                                                }}
                                            />
                                        )}
                                        {/* Recall Overall */}
                                        {box ? (
                                            dataViewKey.split(",").map((ele, i) => {
                                                const overallScoreSum = i === 0 ? recallTopOrMeanKey1 : i === 1 ? recallbottomKey2 : recallThirdKey3;

                                                const displayValue = absoluteToggle ? getFormattedScoreOrNA(overallScoreSum, ele.split(",")[0], absoluteToggle) : getOverallCalculationResult(overallScoreSum, totalSampleSize, totalVisibleProducts, ele);

                                                return (
                                                    <TableCellWithOuterSeparator
                                                        key={`recall-overall-${rowBackendVariable}-${i}`}
                                                        value={displayValue}
                                                        cellType="td"
                                                        testLabel=""
                                                        styles={{
                                                            main: {},
                                                            wrapper: {},
                                                            value: {
                                                                justifyContent: "center",
                                                                background: "#fff",
                                                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                                borderRadius: "5px",
                                                                padding: "5px",
                                                            },
                                                        }}
                                                        showSeperator={ele.startsWith("bottom") ? false : true}
                                                    />
                                                );
                                            })
                                        ) : (
                                            <TableCellWithOuterSeparator
                                                key={`recall-overall-${rowBackendVariable}`}
                                                value={getOverallCalculationResult(recallTopOrMeanKey1, totalSampleSize, totalVisibleProducts, dataViewKey)}
                                                cellType="td"
                                                testLabel=""
                                                styles={{
                                                    main: {},
                                                    wrapper: {},
                                                    value: {
                                                        justifyContent: "center",
                                                        background: "#fff",
                                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                        borderRadius: "5px",
                                                        padding: "5px",
                                                    },
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                                {draggableColumns.map((datum, index) => {
                                    // const key = index + 1;
                                    let recallScore = null;
                                    let conceptScore = null;
                                    let itmBvScore = null;
                                    let recallTest = null;
                                    let conceptTest = null;
                                    let itmTest = null;
                                    let openEndedBackendVariableRecall = "";
                                    let openEndedBackendVariableItm = "";
                                    const productCodeCoreDataId = datum.coreDataId;

                                    if (moduleName === "overall") {
                                        // recallScore = datum.scores.filter((scoreObj) => scoreObj.backendVariable === recallBvObj?.backendVariable)?.[0] || {};
                                        recallScore = getScoreByVariable(datum.scores, recallBvObj?.backendVariable);
                                        openEndedBackendVariableRecall = recallScore?.openEndedBackendVariable || "";
                                        recallTest = getTestResultByVariable(tTestResult, recallBvObj?.backendVariable, productCodeCoreDataId);
                                        if (isITMDataShown) {
                                            //itmBvScore = datum.scores.filter((scoreObj) => scoreObj.backendVariable === itmBvObj?.backendVariable)?.[0] || {};
                                            itmBvScore = getScoreByVariable(datum.scores, itmBvObj?.backendVariable);
                                            openEndedBackendVariableItm = itmBvScore?.openEndedBackendVariable || "";
                                            itmTest = getTestResultByVariable(tTestResult, itmBvObj?.backendVariable, productCodeCoreDataId);
                                        }
                                    } else {
                                        const filteredBvObjScore = kpis.filter((ele) => ele.displayLabel === rowDisplayLabel)?.[0] || {};
                                        recallScore = getScoreByVariable(datum.scores, filteredBvObjScore?.backendVariable);
                                        openEndedBackendVariableRecall = recallScore?.openEndedBackendVariable || "";
                                        recallTest = getTestResultByVariable(tTestResult, filteredBvObjScore?.backendVariable, productCodeCoreDataId);
                                    }

                                    // ==================== CONCEPT/RECALL: resolve concept + recall scores ====================
                                    if (isConceptRecallShown && showConceptData) {
                                        // concept-only: look up the Concept BV directly
                                        const { conceptBvObj } = getConceptAndRecall(rowDisplayLabel, rowBackendVariable, moduleName, kpis);
                                        conceptScore = getScoreByVariable(datum.scores, conceptBvObj?.backendVariable);
                                        conceptTest = getTestResultByVariable(tTestResult, conceptBvObj?.backendVariable, productCodeCoreDataId);
                                    } else if (isConceptRecallShown) {
                                        const { conceptBvObj, recallBvObj: conceptRecallBvObj } = getConceptAndRecall(
                                            rowDisplayLabel,
                                            rowBackendVariable,
                                            moduleName,
                                            kpis
                                        );
                                        conceptScore = getScoreByVariable(datum.scores, conceptBvObj?.backendVariable);
                                        recallScore = getScoreByVariable(datum.scores, conceptRecallBvObj?.backendVariable);
                                        conceptTest = getTestResultByVariable(tTestResult, conceptBvObj?.backendVariable, productCodeCoreDataId);
                                        recallTest = getTestResultByVariable(tTestResult, conceptRecallBvObj?.backendVariable, productCodeCoreDataId);
                                    }

                                    const productCodeTitle = datum.title;

                                    const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);
                                    const recallTestResult = getColorsAndLetter(testAnalysisType, productCodeTitle, recallTest);
                                    const conceptTestResult = getColorsAndLetter(testAnalysisType, productCodeTitle, conceptTest);
                                    const itmTestResult = getColorsAndLetter(testAnalysisType, productCodeTitle, itmTest);

                                    // ==================== CONCEPT DATA ONLY ====================
                                    if (showConceptData) {
                                        return (
                                            <>
                                                {dataViewKey.split(",").map((ele, k) => {
                                                    const key = "concept-only-value-column-" + index + "-" + k;
                                                    const showTestLabel = conceptTest?.test_name !== "student's t test comparisons" && conceptTest?.test_name !== "student's t test comparisons(1-tailed)";
                                                    const testLabel = conceptTestResult?.["label"] ?? null;
                                                    const isKruskallWallis = isBAndW && conceptTest?.test_name?.toLowerCase().includes("kruskall wallis");
                                                    const scoreShow = conceptScore?.[dataViewKey.split(",")[k]];
                                                    return (
                                                        <TableCellWithOuterSeparator
                                                            key={key}
                                                            value={targetColumnIndex === index && hasActuallyMoved ? "" : getFormattedScoreOrNA(scoreShow, ele.split(",")[0])}
                                                            cellType="td"
                                                            testLabel={showTestLabel ? testLabel : ""}
                                                            disableColor={isKruskallWallis}
                                                            mainProps={{
                                                                className: className,
                                                            }}
                                                            styles={{
                                                                main: {
                                                                    backgroundColor: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "#fff",
                                                                    border: targetColumnIndex === index && hasActuallyMoved ? "none" : "",
                                                                    outline: targetColumnIndex === index && hasActuallyMoved ? "none" : "",
                                                                },
                                                                wrapper: {},
                                                                value: {
                                                                    justifyContent: "center",
                                                                    background: conceptTestResult?.["color"]?.[ele] ?? "#fff",
                                                                    boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                                    borderRadius: "5px",
                                                                    padding: "5px",
                                                                },
                                                            }}
                                                            showSeperator={ele.startsWith("bottom") ? false : true}
                                                        />
                                                    );
                                                })}
                                            </>
                                        );
                                    }

                                    // ==================== CONCEPT + RECALL DATA ====================
                                    if (showConceptAndRecallData) {
                                        return (
                                            <>
                                                {dataViewKey.split(",").map((ele, k) => {
                                                    const key = "concept-recall-value-column-" + index + "-" + k;
                                                    const showTestLabel = conceptTest?.test_name !== "student's t test comparisons" && conceptTest?.test_name !== "student's t test comparisons(1-tailed)";
                                                    const testLabel = conceptTestResult?.["label"] ?? null;
                                                    const isKruskallWallis = isBAndW && conceptTest?.test_name?.toLowerCase().includes("kruskall wallis");
                                                    const scoreShow = conceptScore?.[dataViewKey.split(",")[k]];
                                                    return (
                                                        <TableCellWithOuterSeparator
                                                            key={key}
                                                            value={targetColumnIndex === index && hasActuallyMoved ? "" : getFormattedScoreOrNA(scoreShow, ele.split(",")[0])}
                                                            cellType="td"
                                                            testLabel={showTestLabel ? testLabel : ""}
                                                            disableColor={isKruskallWallis}
                                                            mainProps={{
                                                                className: className,
                                                            }}
                                                            styles={{
                                                                main: {
                                                                    backgroundColor: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "#fff",
                                                                    border: targetColumnIndex === index && hasActuallyMoved ? "none" : "",
                                                                    outline: targetColumnIndex === index && hasActuallyMoved ? "none" : "",
                                                                },
                                                                wrapper: {},
                                                                value: {
                                                                    justifyContent: "center",
                                                                    background: conceptTestResult?.["color"]?.[ele] ?? "#fff",
                                                                    boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                                    borderRadius: "5px",
                                                                    padding: "5px",
                                                                },
                                                            }}
                                                            showSeperator={ele.startsWith("bottom") ? false : true}
                                                        />
                                                    );
                                                })}
                                                {dataViewKey.split(",").map((ele, k) => {
                                                    const key = "concept-recall-recall-value-column-" + index + "-" + k;
                                                    const showTestLabel = recallTest?.test_name !== "student's t test comparisons" && recallTest?.test_name !== "student's t test comparisons(1-tailed)";
                                                    const testLabel = recallTestResult?.["label"] ?? null;
                                                    const isKruskallWallis = isBAndW && recallTest?.test_name?.toLowerCase().includes("kruskall wallis");
                                                    const scoreShow = recallScore?.[dataViewKey.split(",")[k]];
                                                    return (
                                                        <TableCellWithOuterSeparator
                                                            key={key}
                                                            value={targetColumnIndex === index && hasActuallyMoved ? "" : getFormattedScoreOrNA(scoreShow, ele.split(",")[0])}
                                                            cellType="td"
                                                            testLabel={showTestLabel ? testLabel : ""}
                                                            disableColor={isKruskallWallis}
                                                            mainProps={{
                                                                className: className,
                                                            }}
                                                            styles={{
                                                                main: {
                                                                    backgroundColor: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "#fff",
                                                                    border: targetColumnIndex === index && hasActuallyMoved ? "none" : "",
                                                                    outline: targetColumnIndex === index && hasActuallyMoved ? "none" : "",
                                                                },
                                                                wrapper: {},
                                                                value: {
                                                                    justifyContent: "center",
                                                                    background: recallTestResult?.["color"]?.[ele] ?? "#fff",
                                                                    boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                                    borderRadius: "5px",
                                                                    padding: "5px",
                                                                },
                                                            }}
                                                            showSeperator={false}
                                                        />
                                                    );
                                                })}
                                            </>
                                        );
                                    }

                                    // ==================== DEFAULT (RECALL DATA or NON-HAIR CARE) ====================
                                    return (
                                        <>
                                            {isITMDataShown && (
                                                <>
                                                    {hasITMTextTheme && renderTTCell(rowHasITMTT, index, "itm", itmBvObj, getOriginalSampleSize(datum), openEndedBackendVariableItm)}
                                                    {dataViewKey.split(",").map((ele, k) => {
                                                        const key = "box-analysis-itm-value-column-" + index + "-" + k;
                                                        const showTestLabel = itmTest?.test_name !== "student's t test comparisons" && itmTest?.test_name !== "student's t test comparisons(1-tailed)";
                                                        const testLabel = itmTestResult?.["label"] ?? null;
                                                        const isKruskallWallis = isBAndW && itmTest?.test_name?.toLowerCase().includes("kruskall wallis");
                                                        return (
                                                            <TableCellWithOuterSeparator
                                                                key={key}
                                                                value={targetColumnIndex === index && hasActuallyMoved ? "" : getFormattedScoreOrNA(itmBvScore[dataViewKey.split(",")[k]], ele.split(",")[0])}
                                                                cellType="td"
                                                                testLabel={showTestLabel ? testLabel : ""}
                                                                disableColor={isKruskallWallis}
                                                                mainProps={{
                                                                    className: className,
                                                                }}
                                                                styles={{
                                                                    main: {
                                                                        backgroundColor: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "#fff",
                                                                        border: targetColumnIndex === index && hasActuallyMoved ? "none" : "",
                                                                        outline: targetColumnIndex === index && hasActuallyMoved ? "none" : "",
                                                                    },
                                                                    wrapper: {},
                                                                    value: {
                                                                        justifyContent: "center",
                                                                        background: itmTestResult?.["color"]?.[ele] ?? "#fff",
                                                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                                        borderRadius: "5px",
                                                                        padding: "5px",
                                                                    },
                                                                }}
                                                                showSeperator={ele.startsWith("bottom") ? false : true}
                                                            />
                                                        );
                                                    })}
                                                </>
                                            )}
                                            {isBeautyAndWellbeing &&
                                                penaltyToggle &&
                                                (() => {
                                                    const leftPenalty = isSkinCare ? Math.abs(parseFloat(recallScore.bottom_penalty)) : parseFloat(recallScore.bottom_penalty);
                                                    const jrPctL = parseFloat(recallScore[dataViewKey.split(",")[1]]) || 0;
                                                    const isBlackStyle = jrPctL >= 75;
                                                    const getPenaltyColor = (p) => {
                                                        if (jrPctL >= 75) return "#000";
                                                        if (p >= 0.75) return "#E14370";
                                                        if (p >= 0.65) return "#D264F4";
                                                        return "#31A7FA";
                                                    };

                                                    return (
                                                        <TableCellWithOuterSeparator
                                                            key={`penalty-left-score-${index}`}
                                                            value={recallScore.bottom_penalty !== null && recallScore.bottom_penalty !== undefined && recallScore.bottom_penalty !== "NaN" && !isNaN(Number(recallScore.bottom_penalty)) ? Number(leftPenalty).toFixed(2) : "NA"}
                                                            cellType="td"
                                                            testLabel=""
                                                            mainProps={{
                                                                className: className,
                                                            }}
                                                            styles={{
                                                                main: {
                                                                    backgroundColor: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "#fff",
                                                                    border: targetColumnIndex === index && hasActuallyMoved ? "none" : "",
                                                                    outline: targetColumnIndex === index && hasActuallyMoved ? "none" : "",
                                                                },
                                                                wrapper: {},
                                                                value: {
                                                                    justifyContent: "center",
                                                                    background: "#fff",
                                                                    color: getPenaltyColor(leftPenalty),
                                                                    border: isBeautyAndWellbeing && !isBlackStyle ? `1px solid ${getPenaltyColor(leftPenalty)}` : "none",
                                                                    boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                                    borderRadius: "5px",
                                                                    padding: "5px",
                                                                    fontWeight: isBeautyAndWellbeing && !isBlackStyle ? "600" : "normal",
                                                                },
                                                            }}
                                                            showSeperator={true}
                                                        />
                                                    );
                                                })()}
                                            {moduleName === "overall"
                                                ? (hasRecallTextTheme && renderTTCell(rowHasRecallTT, index, "recall", recallBvObj, getOriginalSampleSize(datum), openEndedBackendVariableRecall))
                                                : (hasStandardTextTheme && renderTTCell(rowHasStandardTT, index, "standard", filteredBvObjScoreForTT, getOriginalSampleSize(datum), openEndedBackendVariableRecall))
                                            }
                                            {dataViewKey.split(",").map((ele, k) => {
                                                const key = "box-analysis-recall-value-column-" + index + "-" + k;
                                                const showTestLabel = recallTest?.test_name !== "student's t test comparisons" && recallTest?.test_name !== "student's t test comparisons(1-tailed)";
                                                const testLabel = recallTestResult?.["label"] ?? null;
                                                const isKruskallWallis = isBAndW && recallTest?.test_name?.toLowerCase().includes("kruskall wallis");
                                                const initialText = ele.split("_")[0]
                                                const scoreShow = absoluteToggle && box ? recallScore[initialText + "_absolute_count"] : recallScore[dataViewKey.split(",")[k]]
                                                return (
                                                    <TableCellWithOuterSeparator
                                                        key={key}
                                                        //     value={targetColumnIndex === index && hasActuallyMoved ? "" : (() => {
                                                        //     const raw = parseFloat(recallScore[dataViewKey.split(",")[k]]);
                                                        //     return isNaN(raw) ? "NA" : Math.round(raw) + "%";
                                                        // })()}
                                                        value={targetColumnIndex === index && hasActuallyMoved ? "" : getFormattedScoreOrNA(scoreShow, ele.split(",")[0], absoluteToggle)}
                                                        cellType="td"
                                                        testLabel={showTestLabel ? testLabel : ""}
                                                        disableColor={isKruskallWallis}
                                                        mainProps={{
                                                            className: className,
                                                        }}
                                                        styles={{
                                                            main: {
                                                                backgroundColor: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "#fff",
                                                                border: targetColumnIndex === index && hasActuallyMoved ? "none" : "",
                                                                outline: targetColumnIndex === index && hasActuallyMoved ? "none" : "",
                                                            },
                                                            wrapper: {},
                                                            value: {
                                                                justifyContent: "center",
                                                                background: (() => {
                                                                    if (isBeautyAndWellbeing && k === 1) {
                                                                        const jrPct = parseFloat(recallScore[dataViewKey.split(",")[1]]) || 0;
                                                                        return jrPct >= 75 ? "#008000" : "#FFBF00";
                                                                    }
                                                                    if (!penaltyToggle) return recallTestResult?.["color"]?.[ele] ?? "#fff";
                                                                    const jrPct = parseFloat(recallScore[dataViewKey.split(",")[1]]) || 0;
                                                                    if (jrPct >= 75) {
                                                                        if (k === 1) return "#008000";
                                                                        return "#fff";
                                                                    }
                                                                    const tlPct = parseFloat(recallScore[dataViewKey.split(",")[0]]) || 0;
                                                                    const tmPct = parseFloat(recallScore[dataViewKey.split(",")[2]]) || 0;
                                                                    const diff = Math.abs(tlPct - tmPct);
                                                                    if (k === 1) return "#FFBF00";
                                                                    if (k === 0 && tlPct >= 20) return "#FF0000";
                                                                    if (k === 2 && tmPct >= 20) return "#FF0000";
                                                                    return diff >= 10 ? "#FF0000" : "#808080";
                                                                })(),
                                                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                                borderRadius: "5px",
                                                                padding: "5px",
                                                            },
                                                        }}
                                                        showSeperator={ele.startsWith("bottom") ? false : true}
                                                    />
                                                );
                                            })}
                                            {isBeautyAndWellbeing &&
                                                penaltyToggle &&
                                                (() => {
                                                    const rightPenalty = isSkinCare ? Math.abs(parseFloat(recallScore.top_penalty)) : parseFloat(recallScore.top_penalty);
                                                    const jrPctR = parseFloat(recallScore[dataViewKey.split(",")[1]]) || 0;
                                                    const isBlackStyle = jrPctR >= 75;
                                                    const getPenaltyColor2 = (p) => {
                                                        if (jrPctR >= 75) return "#000";
                                                        if (p >= 0.75) return "#E14370";
                                                        if (p >= 0.65) return "#D264F4";
                                                        return "#31A7FA";
                                                    };

                                                    return (
                                                        <TableCellWithOuterSeparator
                                                            key={`penalty-right-score-${index}`}
                                                            value={recallScore.top_penalty !== null && recallScore.top_penalty !== undefined && recallScore.top_penalty !== "NaN" && !isNaN(Number(recallScore.top_penalty)) ? Number(rightPenalty).toFixed(2) : "NA"}
                                                            cellType="td"
                                                            testLabel=""
                                                            mainProps={{
                                                                className: className,
                                                            }}
                                                            styles={{
                                                                main: {
                                                                    backgroundColor: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "#fff",
                                                                    border: targetColumnIndex === index && hasActuallyMoved ? "none" : "",
                                                                    outline: targetColumnIndex === index && hasActuallyMoved ? "none" : "",
                                                                },
                                                                wrapper: {},
                                                                value: {
                                                                    justifyContent: "center",
                                                                    background: "#fff",
                                                                    color: getPenaltyColor2(rightPenalty),
                                                                    border: isBeautyAndWellbeing && !isBlackStyle ? `1px solid ${getPenaltyColor2(rightPenalty)}` : "none",
                                                                    boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                                    borderRadius: "5px",
                                                                    padding: "5px",
                                                                    fontWeight: isBeautyAndWellbeing && !isBlackStyle ? "600" : "normal",
                                                                },
                                                            }}
                                                            showSeperator={false}
                                                        />
                                                    );
                                                })()}
                                        </>
                                    );
                                })}
                            </tr>
                        );
                        const isConceptBv = isConceptVariable(rowBackendVariable, bvObj.variableCategory);
                        const siblingSearchKpis = showConceptAndRecallData ? allMappingsBvs : kpis;
                        const hasSiblingBv = isHairCare && siblingSearchKpis.some((ele) => ele.displayLabel === bvObj.displayLabel && ele.backendVariable !== rowBackendVariable);
                        const bvHasValues = (bv) =>
                            draggableColumns?.some((datum) => {
                                const s = getScoreByVariable(datum.scores, bv);
                                if (!s?.backendVariable) return false;
                                return box ? !!(s[key1] || s[key2] || (key3 && s[key3])) : !!s[key1];
                            });
                        const thisBvHasData = isHairCare && bvHasValues(rowBackendVariable);
                        const siblingHasData = isHairCare && siblingSearchKpis.some((ele) => ele.displayLabel === bvObj.displayLabel && ele.backendVariable !== rowBackendVariable && bvHasValues(ele.backendVariable));
                        const shouldHideForConceptRecall = hasSiblingBv && ((showConceptData && !isConceptBv) || (showRecallData && isConceptBv) || (showConceptAndRecallData && isConceptBv && siblingHasData));
                        const siblingSharesLabelWithData = !shouldHideForConceptRecall && isHairCare && siblingHasData;
                        if (shouldHideForConceptRecall || (siblingSharesLabelWithData && !thisBvHasData)) {
                            return null;
                        }
                        // If this BV has data, never hide it due to NA count (concept BVs with data must show)
                        if (productsScoreWithNA === noOfProducts && !thisBvHasData) {
                            return null;
                        } else if (isLabelOrSiblingRemoved(kpis, bvObj, removedKpiStoreState, moduleName)) {
                            uniqueKpis.add(bvObj.displayLabel);
                            return;
                        } else {
                            uniqueKpis.add(bvObj.displayLabel);
                            ifAvailableFirstRow = false;
                            return row;
                        }
                    } else {
                        return;
                    }
                })
                .filter((ele) => ele);
            let _tr = null;
            if (moduleName === "overall" && _trs.length > 0) {
                _tr = (
                    <>
                        {_trs}
                        <tr className="gap">{gapRow({
                            length: draggableColumns.length,
                            hiddenProductsCount,
                            box,
                            boxAnalysisTypeSelection,
                            isITMDataShown,
                            overallToggle,
                            dataViewKey,
                            targetColumnIndex,
                            hasActuallyMoved,
                            conceptRecallMultiplier
                        })}</tr>
                    </>
                );
            } else {
                _tr = _trs;
            }

            return _tr;
        });
        return _roww;
    };

    const returnJarBoxAnalysisLabelRow = () => {
        return (
            <tr>
                {stickyColumns.map((col, i) => {
                    const isFirst = i === 0;
                    const minWid = isFirst ? (moduleName === "claims" ? 300 : 200) : 300;
                    const maxWid = moduleName === "claims" ? (isFirst ? 300 : 500) : isFirst ? 200 : 300;
                    const leftSpace = isFirst ? 0 : moduleName === "claims" ? 300 : 200;
                    return (
                        <th
                            key={col + i + "jar-box"}
                            scope="col"
                            style={{
                                minWidth: minWid,
                                maxWidth: maxWid,
                                position: "sticky",
                                left: leftSpace,
                                zIndex: 2,
                            }}
                        ></th>
                    );
                })}
                {overallToggle && !showConceptAndRecallData && !["claims"].includes(moduleName) && (
                    <>
                        {penaltyToggle && (
                            <TableCellWithOuterSeparator
                                key="jar-overall-penalty-left-header"
                                value="Penalty"
                                cellType="td"
                                styles={{ main: { minWidth: 150, maxWidth: 150, height: 90, fontWeight: "600" }, wrapper: {}, value: { justifyContent: "center", color: "#005eef", background: "#f0f8ff", boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)", fontWeight: "600", borderRadius: "5px", padding: "5px" } }}
                                showSeperator={false}
                            />
                        )}
                        <TableCellWithOuterSeparator
                            key="jar-overall-recall-too-little-header"
                            value="Too Little"
                            cellType="td"
                            styles={{
                                main: {
                                    minWidth: 150,
                                    maxWidth: 150,
                                    height: 90,
                                    fontWeight: "600",
                                },
                                wrapper: {},
                                value: {
                                    justifyContent: "center",
                                    color: "#005eef",
                                    background: "#f0f8ff",
                                    boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                    fontWeight: "600",
                                    borderRadius: "5px",
                                    padding: "5px",
                                },
                            }}
                            showSeperator={false}
                        />
                        <TableCellWithOuterSeparator
                            key="jar-overall-recall-just-right-header"
                            value="Just Right"
                            cellType="td"
                            styles={{
                                main: {
                                    minWidth: 150,
                                    maxWidth: 150,
                                    height: 90,
                                    fontWeight: "600",
                                },
                                wrapper: {},
                                value: {
                                    justifyContent: "center",
                                    color: "#005eef",
                                    background: "#f0f8ff",
                                    boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                    fontWeight: "600",
                                    borderRadius: "5px",
                                    padding: "5px",
                                },
                            }}
                            showSeperator={false}
                        />
                        <TableCellWithOuterSeparator
                            key="jar-overall-recall-too-much-header"
                            value="Too Much"
                            cellType="td"
                            styles={{
                                main: {
                                    minWidth: 150,
                                    maxWidth: 150,
                                    height: 90,
                                    fontWeight: "600",
                                },
                                wrapper: {},
                                value: {
                                    justifyContent: "center",
                                    color: "#005eef",
                                    background: "#f0f8ff",
                                    boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                    fontWeight: "600",
                                    borderRadius: "5px",
                                    padding: "5px",
                                },
                            }}
                            showSeperator={false}
                        />
                        {penaltyToggle && (
                            <TableCellWithOuterSeparator
                                key="jar-overall-penalty-right-header"
                                value="Penalty"
                                cellType="td"
                                styles={{ main: { minWidth: 150, maxWidth: 150, height: 90, fontWeight: "600" }, wrapper: {}, value: { justifyContent: "center", color: "#005eef", background: "#f0f8ff", boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)", fontWeight: "600", borderRadius: "5px", padding: "5px" } }}
                                showSeperator={false}
                            />
                        )}
                    </>
                )}
                {draggableColumns.map((datum, index) => {
                    const productCodeTitle = datum.title;
                    const productCodeCoreDataId = datum.coreDataId;
                    const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);
                    return (
                        <>
                            {penaltyToggle && (
                                <TableCellWithOuterSeparator
                                    key={`jar-product-penalty-left-${index}`}
                                    value={targetColumnIndex === index && hasActuallyMoved ? "" : "Penalty"}
                                    cellType="td"
                                    testLabel=""
                                    mainProps={{ className }}
                                    styles={{
                                        main: {
                                            minWidth: 150,
                                            maxWidth: 150,
                                            height: "90px",
                                            fontWeight: "600",
                                            cursor: "pointer",
                                            background: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "",
                                        },
                                        wrapper: {},
                                        value: {
                                            justifyContent: "center",
                                            color: "#005eef",
                                            background: "#f0f8ff",
                                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                            fontWeight: "600",
                                            borderRadius: "5px",
                                            padding: "5px",
                                        },
                                    }}
                                    showSeperator={false}
                                />
                            )}
                            <TableCellWithOuterSeparator
                                key={`jar-product-too-little-${index}`}
                                value={targetColumnIndex === index && hasActuallyMoved ? "" : "Too Little"}
                                cellType="td"
                                testLabel=""
                                mainProps={{
                                    className: className,
                                }}
                                styles={{
                                    main: {
                                        minWidth: 150,
                                        maxWidth: 150,
                                        height: "90px",
                                        fontWeight: "600",
                                        cursor: "pointer",
                                        background: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "",
                                    },
                                    wrapper: {},
                                    value: {
                                        justifyContent: "center",
                                        color: "#005eef",
                                        background: "#f0f8ff",
                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                        fontWeight: "600",
                                        borderRadius: "5px",
                                        padding: "5px",
                                    },
                                }}
                                showSeperator={false}
                            />
                            <TableCellWithOuterSeparator
                                key={`jar-product-just-right-${index}`}
                                value={targetColumnIndex === index && hasActuallyMoved ? "" : "Just Right"}
                                cellType="td"
                                testLabel=""
                                mainProps={{
                                    className: className,
                                }}
                                styles={{
                                    main: {
                                        minWidth: 150,
                                        maxWidth: 150,
                                        height: "90px",
                                        fontWeight: "600",
                                        cursor: "pointer",
                                        background: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "",
                                    },
                                    wrapper: {},
                                    value: {
                                        justifyContent: "center",
                                        color: "#005eef",
                                        background: "#f0f8ff",
                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                        fontWeight: "600",
                                        borderRadius: "5px",
                                        padding: "5px",
                                    },
                                }}
                                showSeperator={false}
                            />
                            <TableCellWithOuterSeparator
                                key={`jar-product-too-much-${index}`}
                                value={targetColumnIndex === index && hasActuallyMoved ? "" : "Too Much"}
                                cellType="td"
                                testLabel=""
                                mainProps={{
                                    className: className,
                                }}
                                styles={{
                                    main: {
                                        minWidth: 150,
                                        maxWidth: 150,
                                        height: "90px",
                                        fontWeight: "600",
                                        cursor: "pointer",
                                        background: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "",
                                    },
                                    wrapper: {},
                                    value: {
                                        justifyContent: "center",
                                        color: "#005eef",
                                        background: "#f0f8ff",
                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                        fontWeight: "600",
                                        borderRadius: "5px",
                                        padding: "5px",
                                    },
                                }}
                                showSeperator={false}
                            />
                            {penaltyToggle && (
                                <TableCellWithOuterSeparator
                                    key={`jar-product-penalty-right-${index}`}
                                    value={targetColumnIndex === index && hasActuallyMoved ? "" : "Penalty"}
                                    cellType="td"
                                    testLabel=""
                                    mainProps={{ className }}
                                    styles={{
                                        main: {
                                            minWidth: 150,
                                            maxWidth: 150,
                                            height: "90px",
                                            fontWeight: "600",
                                            cursor: "pointer",
                                            background: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "",
                                        },
                                        wrapper: {},
                                        value: {
                                            justifyContent: "center",
                                            color: "#005eef",
                                            background: "#f0f8ff",
                                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                            fontWeight: "600",
                                            borderRadius: "5px",
                                            padding: "5px",
                                        },
                                    }}
                                    showSeperator={false}
                                />
                            )}
                        </>
                    );
                })}
            </tr>
        );
    };

    // column drag and drop logic
    const handleColumnDragStart = (e, index) => {
        e.dataTransfer.setData("columnIndex", index);
        // Create an invisible image to replace the default drag image
        const img = new Image();
        img.src = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP88xIAAX8B5sR8D6cAAAAASUVORK5CYII=";
        e.dataTransfer.setDragImage(img, 0, 0);
        setDraggedColumnIndex(index);
        const headerRect = e.target.getBoundingClientRect();
        setMousePosition({
            x: e.clientX - dragOffset.x,
            y: e.clientY - dragOffset.y,
        });

        setDragOffset({
            x: e.clientX - headerRect.left,
            y: e.clientY - headerRect.top,
        });

        setDraggedColumnDiamension({
            width: headerRect.width,
            height: headerRect.height,
        });
        setIsDragging(true);
        initialMousePos.current = { x: e.clientX, y: e.clientY };
        setHasActuallyMoved(false);
    };

    const handleColumnDrop = (index) => {
        const updatedColumns = [...draggableColumns];
        const [draggedColumn] = updatedColumns.splice(draggedColumnIndex, 1);
        updatedColumns.splice(index, 0, draggedColumn);

        setDraggableColumns(updatedColumns);
        setDraggedColumnIndex(null);
        setIsDragging(false);

        const _updateArray = updatedColumns.map((ele) => {
            return {
                productCode: ele.title,
                coreDataId: ele.coreDataId,
                isCompetitor: ele.isCompetitorProduct,
            };
        });
        dispatch(loadProductOrderState([..._updateArray]));
        setHasActuallyMoved(false);
        setTargetColumnIndex(null);
        initialMousePos.current = null;
        setDraggedColumnDiamension({
            width: null,
            height: null,
        });
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        if (!isDragging) return;

        if (!hasActuallyMoved && initialMousePos.current) {
            const dx = Math.abs(e.clientX - initialMousePos.current.x);
            const dy = Math.abs(e.clientY - initialMousePos.current.y);
            if (dx > 5 || dy > 5) {
                setHasActuallyMoved(true);
            }
        }

        setMousePosition({
            x: e.clientX - dragOffset.x,
            y: e.clientY - dragOffset.y,
        });

        if (!hasActuallyMoved) return;

        const headers = Array.from(tableRef.current.querySelectorAll("th")).filter((header) => header.className.includes("checked--"));
        const draggedRect = draggedElementRef.current?.getBoundingClientRect();

        if (!draggedRect) return;

        const closestHeader = headers.reduce((closest, header, index) => {
            if (index === draggedColumnIndex) return closest;

            const rect = header.getBoundingClientRect();
            const center = rect.left + rect.width / 2;
            const distance = Math.abs(e.clientX - center);

            if (!closest || distance < closest.distance) {
                return { index, distance };
            }
            return closest;
        }, null);
        if (closestHeader) {
            if (draggedRect.left + draggedRect.width / 2 < headers[closestHeader.index].getBoundingClientRect().left + headers[closestHeader.index].getBoundingClientRect().width / 2) {
                if (hasActuallyMoved) {
                    setTargetColumnIndex(closestHeader.index);
                }
            }
        }
    };

    const clearAllDragVars = () => {
        setDraggedColumnIndex(null);
        setIsDragging(false);
        setHasActuallyMoved(false);
        setTargetColumnIndex(null);
        initialMousePos.current = null;
        setDraggedColumnDiamension({
            width: null,
            height: null,
        });
    };
    const handleProductClick = (e, info) => {
        setProductDetailsDialogueOpen(true);
        setSelectedProductInfo(info);
    };
    return (
        <div>
            <ColumnVisibility
                productsSecondaryData={draggableColumns}
                pageName={moduleName}
            />
            <div
                className="table-responsive"
                style={{
                    width: "100%",
                    position: "relative",
                    minHeight: "584px",
                    top: -25,
                }}
            >
                <table
                    ref={tableRef}
                    onMouseLeave={clearAllDragVars}
                    className="table text-sm analysis-table"
                    style={{
                        background: "transparent",
                        borderCollapse: "separate",
                        borderSpacing: "0",
                    }}
                >
                    <thead>{returnTableHeader()}</thead>
                    <tbody ref={tbodyRef}>
                        {returnConsumerFilterRow()}
                        {isITMDataShown && !showConceptAndRecallData && returnItmRecallLabelRow()}
                        {showConceptAndRecallData && returnConceptRecallLabelRow()}
                        {box && returnBoxAnalysisLabelRow()}
                        {isBeautyAndWellbeing && !showConceptAndRecallData && returnJarBoxAnalysisLabelRow()}
                        {!isITMDataShown && !box && !isBeautyAndWellbeing && (moduleName === "overall" ? hasRecallTextTheme : hasStandardTextTheme) && returnTextThemeLabelRow()}
                        {returnAnalysisScoresRow()}
                    </tbody>
                </table>
            </div>
            {isDragging && draggedColumnIndex !== null && hasActuallyMoved && (
                <DragRenderTable
                    dragSettings={{
                        draggableColumns: draggableColumns,
                        draggedColumnIndex: draggedColumnIndex,
                        mousePosition: mousePosition,
                        draggedColumnDiamension: draggedColumnDiamension,
                        // tableColSpan: tableColSpan,
                        tableHeadSpan: tableHeadSpan,
                        draggedElementRef: draggedElementRef,
                    }}
                    analysisView={{
                        box: box,
                        isITMDataShown: isITMDataShown,
                        moduleName: moduleName,
                        analysisTypeValue: analysisTypeValue,
                        boxAnalysisTypeSelection: boxAnalysisTypeSelection,
                        penaltyToggle: penaltyToggle,
                    }}
                    productData={{
                        noOfProducts: noOfProducts,
                    }}
                    tableData={{
                        tableDataMapping: tableDataMapping,
                        isConsumerFilterLoadingStoreData: isConsumerFilterLoadingStoreData,
                        removedKpiStoreState: removedKpiStoreState,
                        dataViewKey: dataViewKey,
                        tTestResult: tTestResult,
                    }}
                    textThemeData={{
                        hasITMTextTheme: hasITMTextTheme,
                        hasRecallTextTheme: hasRecallTextTheme,
                        hasStandardTextTheme: hasStandardTextTheme,
                        enableTextThemeAnalysis: enableTextThemeAnalysis,
                        TTIconWithTooltip: TTIconWithTooltip,
                        textThemeHeaderStyles: textThemeHeaderStyles,
                        checkedTTBoxes: textThemeSelections,
                    }}
                />
            )}
            {isBeautyAndWellbeing && (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "10px 4px", fontSize: "11px" }}>
                    <div style={{ display: "flex", gap: "20px", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <span style={{ width: 14, height: 14, borderRadius: 3, background: "#008000", display: "inline-block" }} />
                            <span style={{ fontWeight: 700 }}>Just Right {">="} 75% → Indicates high performance / optimal level</span>
                        </span>
                        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <span style={{ width: 14, height: 14, borderRadius: 3, background: "#FFBF00", display: "inline-block" }} />
                            <span style={{ fontWeight: 700 }}>Just Right &lt; 75% → Potential optimization opportunity</span>
                        </span>

                        {penaltyToggle && (
                            <>
                                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                    <span style={{ width: 14, height: 14, borderRadius: 3, background: "#FF0000", display: "inline-block" }} />
                                    <span style={{ fontWeight: 700 }}>
                                        Tail Difference {">="} 10% or any individual tail value {">="} 20% → Indicates a significant consumer concern or clear directional penalty
                                    </span>
                                </span>
                                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                    <span style={{ width: 14, height: 14, borderRadius: 3, background: "#808080", display: "inline-block" }} />
                                    <span style={{ fontWeight: 700 }}>Tail Difference &lt; 10% → No clear penalty direction</span>
                                </span>
                            </>
                        )}
                    </div>
                    {penaltyToggle && (
                        <div style={{ display: "flex", gap: "40px", alignItems: "center", justifyContent: "center" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                <span style={{ width: 14, height: 14, borderRadius: 3, background: "#E14370", display: "inline-block" }} />
                                <span style={{ fontWeight: 700 }}>Primary Penalty (High-priority fix)</span>
                            </span>
                            <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                <span style={{ width: 14, height: 14, borderRadius: 3, background: "#D264F4", display: "inline-block" }} />
                                <span style={{ fontWeight: 700 }}>Secondary penalty (medium-priority fix)</span>
                            </span>
                            <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                <span style={{ width: 14, height: 14, borderRadius: 3, background: "#31A7FA", display: "inline-block" }} />
                                <span style={{ fontWeight: 700 }}>Not meaningful (Low impact)</span>
                            </span>
                        </div>
                    )}
                </div>
            )}
            <RemoveRestoreDialog
                settings={{
                    openRestoreDialog: openRestoreDialog,
                    removedKpi: removedKpi,
                    showCheckBox: showCheckBox,
                    disableRestore: disableRestore,
                }}
                functionsSettings={{
                    setShowCheckBox: setShowCheckBox,
                    setOpenRestoreDialog: setOpenRestoreDialog,
                }}
                analysisView={{
                    moduleName: moduleName,
                    mappings: tableDataMapping,
                }}
            />
            {!["overall", "claims", "fragranceMetrics"].includes(moduleName) && (
                <SkewTabs
                    tableData={{
                        productsSecondaryDataState: productsSecondaryData,
                    }}
                    settings={{
                        activeTab: activeTab,
                        setActiveTab: setActiveTab,
                    }}
                    analysisView={{
                        moduleName: moduleName,
                        mappings: mappings,
                        analysisTypeValue: analysisTypeValue,
                        boxAnalysisTypeSelection: boxAnalysisTypeSelection,
                    }}
                />
            )}
            <AnalysisTableBottomLabel
                actionStandard={{
                    actionStandardKpis: actionStandardKpis,
                    actionStandardData: actionStandardData,
                    showActionStandardsInfo: !((isSkinCare && role?.toLowerCase() === "user")) && ((moduleName === "overall" || (moduleName === "agreement" && isBAndW) || (moduleName === "justRight" && (isHairCare || isUnileverFragranceHouse))) ? true : false),
                }}
                tTest={{
                    tTestLegends: tTestLegends,
                    isFisher: isFisher,
                    tTestResult: tTestResult,
                    testLabel: testLabel,
                }}
                analysisView={{
                    moduleName: moduleName,
                }}
            />
            {productDetailsDialogueOpen && (
                <ProductInfo
                    settings={{
                        productDetailsDialogueOpen: productDetailsDialogueOpen,
                        setProductDetailsDialogueOpen: setProductDetailsDialogueOpen,
                    }}
                    info={selectedProductInfo}
                    productsSecondaryData={productsSecondaryData}
                />
            )}
            {hasTextThemeSelections && (
                <div style={{ padding: "10px 4px", fontSize: "12px", textAlign: "left", marginTop: "-10px", color: "#495057", fontWeight: 600 }}>
                    <strong>Note:</strong> Deselect &apos;Theme Text Analysis Selection boxes&apos; in order to reactivate other analysis module options in the &apos;Navigation&apos; button.
                </div>
            )}
            {showSampleSizeNote && (
                <div style={{ padding: "10px 4px", fontSize: "12px", textAlign: "left", marginTop: "10px", color: "#495057", fontWeight: 600 }}>
                    <strong>Note:</strong> Theme Text Analysis cannot be performed when the sample is equal or below 10 respondents, in line with Unilever’s AI ethics and data protection principles, to reduce re-identification risk
                </div>
            )}
        </div>
    );
}

AnalysisTable.propTypes = {
    productsSecondaryData: PropTypes.array,
    isITMDataShown: PropTypes.bool,
    tTest: PropTypes.object,
    handleFilterSubmit: PropTypes.func,
    analysisLevelData: PropTypes.object,
    settings: PropTypes.object,
};
export default AnalysisTable;
